import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { Bar, Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineController,
  LineElement,
  PointElement,
  Tooltip,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import {
  CLIMATE_DEFAULT_SMOOTHING_M,
  CLIMATE_MONTH_LABELS,
  CLIMATE_PERIODS,
  CLIMATE_Y_OFFSET,
  HORIZON_COLORS,
  applyClimateStationTransform,
  climateDomain,
  climateValueAt,
  climateValues,
  createClimateInterpolator,
  createClimateStations,
  formatClimateValue,
  horizonLabel,
  paintClimateStations,
  parseClimateAxes,
  periodLabel,
  stationMonthlySeries,
  stationScalars,
} from "./climateLayer";
import { loadDataset } from "./datapack";
import { TERRITORIES_URL, territoryUrl } from "./terrainBase";
import {
  COMMUNE_BORDER_Y_OFFSET,
  COMMUNE_CHOROPLETH_Y_OFFSET,
  COMMUNE_NODATA,
  applyDrapedExaggeration,
  buildCommuneBorders,
  buildCommuneCellIndex,
  communeHoverColor,
  createTerrainPicker,
  paintCells,
  paintRasterColors,
} from "./communesLayer";
import {
  GEOLOGY_Y_OFFSET,
  geologyEntityAt,
} from "./geologyLayer";
import {
  cityCatalog,
  createCityMarker,
  disposeCityMarker,
  occludeCityLabels,
  selectCities,
} from "./citiesLayer";
import {
  LAYER_ORDER,
  LAYER_SPECS,
  defaultLayerSettings,
  layerLabel,
  mergeLayerSettings,
} from "./layerSpecs";
import { LayerControlGroup } from "./LayerControls";
import ZoomOverlay from "./ZoomOverlay";
import {
  DISCHARGE_MONTH_LABELS,
  applyDischargeStationTransform,
  createDischargeStations,
  dischargeValueAt,
  formatDischargeValue,
  paintDischargeStations,
  parseDischargeAxes,
  stationMonthlyDischarge,
} from "./dischargeLayer";
import {
  buildLegend,
  findIndicator,
  formatIndicatorValue,
  formatNumber,
  groupedIndicators,
  rampCss,
  resolveIndicator,
} from "./indicatorEngine";
import "../terrain_test/TerrainTestPage.css";
import "./TerrainViewer.css";

// Enregistrement explicite (idempotent) : ne pas dependre du fait qu'un autre
// composant de l'appli ait deja enregistre ce plugin globalement.
ChartJS.register(
  CategoryScale, LinearScale, BarElement,
  LineController, LineElement, PointElement,
  Tooltip, ChartDataLabels
);

/**
 * Visualisation 3D d'un territoire (terrain + calques rivieres/villes/
 * prelevements/geologie/pluviometrie), reutilisable — extrait du pilote
 * `terrain_test/TerrainTestPage.js`. Contrairement au pilote, ce composant
 * n'expose aucun reglage de developpement (exageration, filaire, calibrage
 * des sliders...) : ce sont des constantes internes fixees aux valeurs
 * deja eprouvees. Piloté par props :
 *   - territoryId : sous-dossier de /terrain3d/ a charger.
 *   - availableLayers : calques a recuperer/construire (changer cette
 *     valeur reconstruit la scene — a n'utiliser qu'a l'authoring, pas
 *     pendant un cheminement).
 *   - activeLayers : sous-ensemble visible de availableLayers, peut changer
 *     librement (ex: d'une etape a l'autre d'un module) sans reconstruire.
 *   - cameraPose : { azimuth, elevation, distance, target:{x,y,z} } (radians,
 *     distances terrain). Si fourni, la camera demarre/se deplace vers cette
 *     vue ; ensuite libre a l'utilisateur d'orbiter (aucun verrouillage).
 * Une ref (forwardRef) expose { getCurrentPose(), applyPose(pose) } pour la
 * capture de vue depuis le builder.
 */

const DEFAULT_CAMERA_AZIMUTH = THREE.MathUtils.degToRad(30.5);
const DEFAULT_CAMERA_ELEVATION = THREE.MathUtils.degToRad(42);
const DEFAULT_CAMERA_DISTANCE_RATIO = 0.77; // x worldDiag, utilise seulement si aucune cameraPose n'est fournie

// Reglages fixes qui etaient des sliders de reglage dans le pilote — valeurs
// reprises telles quelles (deja eprouvees visuellement).
const FIXED_EXAGGERATION = 2.5;

// --- Coût de rendu -------------------------------------------------------
//
// CE MODULE TOURNE SUR LE PORTABLE D'UN ANIMATEUR, pendant deux heures, en
// projection. Les trois constantes ci-dessous sont les garde-fous qui évitent
// qu'une carte posée à l'écran fasse chauffer la machine pour rien. Elles se
// règlent ensemble : baisser la définition et la cadence au repos coûte peu à
// l'œil, beaucoup moins à la carte graphique.

// DÉFINITION DE RENDU. Sur un écran Retina, `devicePixelRatio` vaut 2 : en
// plein écran on rendait ~8 millions de pixels par image, MSAA en plus. Or la
// sortie utile est un vidéoprojecteur en 1080p — ces pixels-là sont calculés
// puis jetés. À 1,5 on garde le lissage des arêtes du relief (le seul endroit
// où l'escalier se verrait de loin) pour 45 % de surface en moins.
const MAX_PIXEL_RATIO = 1.5;

// CADENCE AU REPOS. Rien dans cette scène n'exige la fréquence de l'écran
// quand personne ne la touche : seuls le défilement de la texture des
// rivières et la respiration des pastilles animent en continu, tous deux à
// ~1,5 Hz. La cadence pleine est rendue dès qu'on manipule la carte ou qu'un
// vol de caméra est en cours — c'est là, et là seulement, que la fluidité se
// voit.
const IDLE_FPS = 30;

// Durée pendant laquelle la carte reste considérée « manipulée » après le
// dernier événement de pointeur. Couvre l'inertie d'OrbitControls
// (dampingFactor 0,08 : le mouvement s'éteint en une quarantaine d'images),
// qui n'émet aucun événement mais doit rester fluide jusqu'au bout.
const ACTIVITY_TAIL_MS = 700;

function poseToCameraPosition(pose) {
  const { azimuth, elevation, distance } = pose;
  const t = pose.target || { x: 0, y: 0, z: 0 };
  return {
    x: t.x + distance * Math.cos(elevation) * Math.sin(azimuth),
    y: t.y + distance * Math.sin(elevation),
    z: t.z + distance * Math.cos(elevation) * Math.cos(azimuth),
  };
}

function applyPoseToScene(camera, controls, pose) {
  if (!pose || pose.distance == null) return;
  const t = pose.target || { x: 0, y: 0, z: 0 };
  const p = poseToCameraPosition(pose);
  camera.position.set(p.x, p.y, p.z);
  controls.target.set(t.x, t.y, t.z);
  controls.update();
}

// --- Transitions de vue ---------------------------------------------------
//
// POURQUOI ANIMER. Passer d'une etape a l'autre TELEPORTAIT la camera. Sur un
// ecran projete devant une salle, ce saut sec reduit le module a un diaporama
// de captures d'ecran : les participants doivent se reorienter a chaque etape,
// sans savoir ou ils viennent d'atterrir ni par rapport a quoi. Un survol
// continu, lui, montre le chemin — c'est la meme information, mais rattachee a
// une geographie.
const POSE_TRANSITION_MS = 1200;

// Cubique symetrique : depart et arrivee sans a-coup. Une interpolation
// lineaire donnerait un demarrage brutal, tres visible sur un mouvement lent.
function easeInOutCubic(t) {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

// L'azimut est un angle : entre 350 deg et 10 deg, le chemin court fait 20 deg
// et non 340. Sans ce repli, la camera ferait un tour complet du territoire
// pour rejoindre une vue voisine.
function shortestAngleDelta(from, to) {
  let delta = (to - from) % (Math.PI * 2);
  if (delta > Math.PI) delta -= Math.PI * 2;
  if (delta < -Math.PI) delta += Math.PI * 2;
  return delta;
}

function lerpPose(from, to, k) {
  const fromTarget = from.target || { x: 0, y: 0, z: 0 };
  const toTarget = to.target || { x: 0, y: 0, z: 0 };
  return {
    azimuth: from.azimuth + shortestAngleDelta(from.azimuth, to.azimuth) * k,
    elevation: from.elevation + (to.elevation - from.elevation) * k,
    distance: from.distance + (to.distance - from.distance) * k,
    target: {
      x: fromTarget.x + (toTarget.x - fromTarget.x) * k,
      y: fromTarget.y + (toTarget.y - fromTarget.y) * k,
      z: fromTarget.z + (toTarget.z - fromTarget.z) * k,
    },
  };
}

/** Avance la transition en cours. Renvoie true tant qu'elle n'est pas finie. */
function stepPoseTween(state, camera, controls, now) {
  const tween = state.poseTween;
  if (!tween) return false;
  const raw = Math.min(1, (now - tween.startedAt) / tween.duration);
  applyPoseToScene(camera, controls, lerpPose(tween.from, tween.to, easeInOutCubic(raw)));
  if (raw >= 1) state.poseTween = null;
  return true;
}

// --- Recentrage optique quand une partie du canvas est masquee par l'UI ---
// Quand un panneau recouvre une bande du canvas, le terrain reste centre sur
// le viewport ENTIER et se retrouve donc a moitie sous le panneau. On decale
// le tronc de vision ("lens shift") pour le recentrer dans la zone restee
// visible : setViewOffset cisaille le frustum sans toucher au champ de vision
// (aucune deformation), et est pris en compte aussi bien par le raycasting
// (Raycaster.setFromCamera) que par les projections ecran des marqueurs
// (Vector3.project) — donc les clics et les panneaux ancres suivent.
function insetsToViewShift(insets, width, height) {
  if (!insets || !width || !height) return { dx: 0, dy: 0 };
  // Bornes : au-dela de 60% du canvas masque, on arrete de decaler, sinon le
  // terrain sortirait du cadre de l'autre cote.
  const clamp = (v, max) => Math.max(0, Math.min(v || 0, max));
  const left = clamp(insets.left, width * 0.6);
  const right = clamp(insets.right, width * 0.6);
  const top = clamp(insets.top, height * 0.6);
  const bottom = clamp(insets.bottom, height * 0.6);
  return { dx: (left - right) / 2, dy: (top - bottom) / 2 };
}

function applyCameraViewShift(camera, width, height, dx, dy) {
  if (!width || !height) return;
  if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5) {
    if (camera.view && camera.view.enabled) camera.clearViewOffset();
    return;
  }
  camera.setViewOffset(width, height, -dx, -dy, width, height);
}

function readPoseFromScene(camera, controls) {
  const target = controls.target;
  const offset = camera.position.clone().sub(target);
  const distance = offset.length() || 1;
  const elevation = Math.asin(THREE.MathUtils.clamp(offset.y / distance, -1, 1));
  const azimuth = Math.atan2(offset.x, offset.z);
  return { azimuth, elevation, distance, target: { x: target.x, y: target.y, z: target.z } };
}

// Rampe hypsometrique : basse altitude (fond de vallee / cours d'eau) vers
// haute altitude (crete calcaire). Chaque stop = [t, [r,g,b] 0-255].
const ELEVATION_RAMP = [
  [0.0, [43, 98, 92]],
  [0.15, [72, 128, 68]],
  [0.38, [123, 148, 74]],
  [0.6, [150, 122, 84]],
  [0.82, [150, 143, 132]],
  [1.0, [238, 232, 216]],
];

// Hauteur du plan de survol des rivieres, en fraction de l'amplitude
// altimetrique au-dessus du point le plus bas (cf. riverPickPlane).
const RIVER_PICK_HEIGHT_RATIO = 0.035;

// Teinte du "socle" (mur + base) : terre/roche, degrade clair -> sombre.
const WALL_TOP_COLOR = [120, 98, 76];
const WALL_BOTTOM_COLOR = [46, 38, 32];

function rampColor(t, ramp) {
  t = Math.min(1, Math.max(0, t));
  for (let i = 0; i < ramp.length - 1; i++) {
    const [t0, c0] = ramp[i];
    const [t1, c1] = ramp[i + 1];
    if (t >= t0 && t <= t1) {
      const f = (t - t0) / (t1 - t0 || 1);
      return [
        (c0[0] + (c1[0] - c0[0]) * f) / 255,
        (c0[1] + (c1[1] - c0[1]) * f) / 255,
        (c0[2] + (c1[2] - c0[2]) * f) / 255,
      ];
    }
  }
  const last = ramp[ramp.length - 1][1];
  return [last[0] / 255, last[1] / 255, last[2] / 255];
}

// Rampe pluviometrie (donnees synthetiques — a remplacer par une vraie
// grille meteo, cf. commentaire plus bas), convention "carte meteo" : sec =
// jaune/beige -> vert -> bleu -> violet pour les zones les plus arrosees.
function lerpColor(c0, c1, f) {
  return [
    (c0[0] + (c1[0] - c0[0]) * f) / 255,
    (c0[1] + (c1[1] - c0[1]) * f) / 255,
    (c0[2] + (c1[2] - c0[2]) * f) / 255,
  ];
}

// Profondeur (en metres reels, non exageres) du socle sous le point le plus bas du terrain.
const SOCLE_DEPTH_RATIO = 0.16; // x zRange

// --- Rivieres : rendu generaliste (fonctionne avec n'importe quel nombre
// de cours d'eau, chacun pouvant avoir plusieurs polylignes disjointes) ---
const RIVER_COLOR = "#8fd8ee";

function createFlowTexture() {
  const canvas = document.createElement("canvas");
  canvas.width = 64;
  canvas.height = 8;
  const ctx = canvas.getContext("2d");
  const grad = ctx.createLinearGradient(0, 0, 64, 0);
  grad.addColorStop(0, "rgba(200,240,255,0.1)");
  grad.addColorStop(0.5, "rgba(225,250,255,0.95)");
  grad.addColorStop(1, "rgba(200,240,255,0.1)");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, 64, 8);
  const tex = new THREE.CanvasTexture(canvas);
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.ClampToEdgeWrapping;
  return tex;
}

// --- Geometrie du ruban ---------------------------------------------------
//
// POURQUOI LES RUBANS SE FROISSAIENT. Un ruban est une extrusion : a chaque
// point de la polyligne on ecarte deux sommets de part et d'autre. Or la
// donnee source est BEAUCOUP plus fine que le ruban dessine — 9 m entre deux
// points sur la Loire, pour un ruban large de 1 600 m. Des qu'un meandre
// tournait plus court que la demi-largeur, les sommets du bord interieur se
// croisaient : les quads se repliaient en nœuds papillon, et comme le
// materiau est translucide sans ecriture de profondeur, chaque repliement
// s'ajoutait au precedent — d'ou les eclats blancs en epingle.
//
// Trois gestes, dans cet ordre :
//  1. REECHANTILLONNER au pas de la largeur dessinee. Un ruban large n'a pas
//     le droit de suivre des meandres plus serres que lui : les lui faire
//     suivre ne montre aucun detail supplementaire, ca fabrique du bruit.
//  2. LISSER ce qui reste d'anguleux.
//  3. MITRER les jointures. Ecarter les sommets d'exactement la demi-largeur
//     retrecit le ruban dans les virages — il y faut 1/cos(θ/2).

// Pas de reechantillonnage, en fraction de la demi-largeur du ruban.
const RIVER_RESAMPLE_RATIO = 0.9;
// Plancher : inutile d'echantillonner plus fin que la donnee source (~10 m
// entre deux points), et les petits ruisseaux gardent ainsi leur trace.
const RIVER_MIN_RESAMPLE_STEP = 25;
const RIVER_SMOOTHING_PASSES = 2;
// Au-dela, la mitre est tronquee : dans une epingle a cheveux, 1/cos(θ/2)
// tend vers l'infini et produirait une aiguille de plusieurs kilometres.
const RIVER_MITER_LIMIT = 2.5;
// Repos du curseur d'epaisseur avant de refaire la geometrie a la bonne
// finesse. Assez court pour passer pour immediat, assez long pour qu'un
// glisser continu n'en declenche qu'une seule.
const RIVER_REBUILD_DELAY_MS = 250;

/** Reechantillonne une polyligne a pas constant. Les extremites sont gardees. */
function resampleLine(line, step) {
  if (line.length < 2 || !(step > 0)) return line;
  const out = [line[0]];
  let acc = 0; // distance parcourue depuis le dernier point pose
  for (let i = 1; i < line.length; i++) {
    const [ax, ay] = line[i - 1];
    const [bx, by] = line[i];
    const segLen = Math.hypot(bx - ax, by - ay);
    if (segLen === 0) continue;
    let pos = 0;
    while (acc + (segLen - pos) >= step) {
      pos += step - acc;
      out.push([ax + ((bx - ax) * pos) / segLen, ay + ((by - ay) * pos) / segLen]);
      acc = 0;
    }
    acc += segLen - pos;
  }
  // Fermer la ligne sur son vrai dernier point, sauf s'il tombe si pres du
  // precedent qu'il creerait un segment quasi nul — donc une normale indefinie.
  const last = line[line.length - 1];
  const tail = out[out.length - 1];
  if (Math.hypot(last[0] - tail[0], last[1] - tail[1]) > step * 0.25) out.push(last);
  return out;
}

/** Moyenne mobile 1-2-1, extremites fixes. */
function smoothLine(line, passes) {
  let cur = line;
  for (let p = 0; p < passes && cur.length >= 3; p++) {
    const next = [cur[0]];
    for (let i = 1; i < cur.length - 1; i++) {
      next.push([
        (cur[i - 1][0] + cur[i][0] * 2 + cur[i + 1][0]) / 4,
        (cur[i - 1][1] + cur[i][1] * 2 + cur[i + 1][1]) / 4,
      ]);
    }
    next.push(cur[cur.length - 1]);
    cur = next;
  }
  return cur;
}

// COMBIEN DE SOMMETS EN TRAVERS DU RUBAN.
//
// Deux suffisent tant que le ruban est plus etroit qu'une maille de terrain :
// sa section est alors trop courte pour que le relief y varie. Au-dela, une
// section a deux sommets tend une corde d'un bord a l'autre — pour la Loire,
// large de 1 600 m sur une grille a 200 m, ce serait un pont d'un versant a
// l'autre passant plusieurs dizaines de metres au-dessus du lit. On subdivise
// donc jusqu'a suivre le relief en travers, sans jamais depasser neuf rangs :
// au-dela on paierait des sommets pour un gain que personne ne verrait.
function ribbonRows(halfWidth, cellsize) {
  return Math.max(2, Math.min(9, Math.ceil((halfWidth * 2) / cellsize) + 1));
}

// Redimensionne un ruban de riviere deja construit (les sommets sont
// recalcules a partir des points centraux et de leurs normales) : evite de
// reconstruire la geometrie a chaque mouvement de curseur.
//
// Chaque sommet est REDRAPE au passage. Ils portaient tous l'altitude du point
// central : un ruban large posait donc une dalle horizontale en travers de la
// vallee, qui rentrait dans le versant d'un cote et flottait de l'autre.
function applyRiverWidth(ribbon, halfWidth, drape) {
  const { geometry, points, normals, rawH, rows } = ribbon;
  const pos = geometry.attributes.position;
  for (let i = 0; i < points.length; i++) {
    const p = points[i];
    const nx = normals[i * 2];
    const nz = normals[i * 2 + 1];
    for (let k = 0; k < rows; k++) {
      // -1 sur un bord, +1 sur l'autre, reparti regulierement entre les deux.
      const t = (k / (rows - 1)) * 2 - 1;
      const vx = p.x + nx * halfWidth * t;
      const vz = p.z + nz * halfWidth * t;
      const idx = i * rows + k;
      pos.setX(idx, vx);
      pos.setZ(idx, vz);
      const h = drape.heightAtWorld(vx, vz);
      rawH[idx] = h == null ? p.rawH : h;
    }
  }
  pos.needsUpdate = true;
  applyRiverExaggeration(geometry, rawH, drape.exaggeration);
}

// Normales de jointure, NON UNITAIRES : leur longueur porte le facteur de
// mitre, ce qui laisse `applyRiverWidth` s'ecrire « point + normale x
// demi-largeur » sans rien savoir de la geometrie des virages.
function computeRiverNormals(points) {
  const n = points.length;
  const normals = new Float32Array(n * 2);

  // Normale du segment i -> i+1. La derniere repete l'avant-derniere pour que
  // les deux extremites se traitent comme un point droit (mitre neutre).
  const segNx = new Float32Array(n);
  const segNz = new Float32Array(n);
  for (let i = 0; i < n - 1; i++) {
    let dx = points[i + 1].x - points[i].x;
    let dz = points[i + 1].z - points[i].z;
    const len = Math.hypot(dx, dz) || 1;
    dx /= len;
    dz /= len;
    segNx[i] = -dz;
    segNz[i] = dx;
  }
  segNx[n - 1] = segNx[n - 2];
  segNz[n - 1] = segNz[n - 2];

  for (let i = 0; i < n; i++) {
    const ax = i > 0 ? segNx[i - 1] : segNx[0];
    const az = i > 0 ? segNz[i - 1] : segNz[0];
    const bx = segNx[i];
    const bz = segNz[i];
    let mx = ax + bx;
    let mz = az + bz;
    const mLen = Math.hypot(mx, mz);
    if (mLen < 1e-6) {
      // Demi-tour exact : la bissectrice est indefinie, on garde la normale
      // du segment sortant plutot que de diviser par zero.
      normals[i * 2] = bx;
      normals[i * 2 + 1] = bz;
      continue;
    }
    mx /= mLen;
    mz /= mLen;
    // La bissectrice doit etre allongee de 1/cos(θ/2) pour que le ruban garde
    // sa largeur dans le virage ; cos se lit sur la normale du segment.
    const cos = mx * bx + mz * bz;
    const scale = Math.min(RIVER_MITER_LIMIT, 1 / Math.max(cos, 1e-3));
    normals[i * 2] = mx * scale;
    normals[i * 2 + 1] = mz * scale;
  }
  return normals;
}

// Ne pose ici que ce qui ne depend PAS de la largeur : les coordonnees de
// texture et le maillage. Les sommets eux-memes sont laisses a
// `applyRiverWidth`, qui est de toute façon rappele a chaque reglage — deux
// ecritures du meme calcul, c'etait deux occasions de les voir diverger.
function buildRiverRibbon(points, normals, halfWidth, bandWorldLength, drape, rows) {
  const n = points.length;
  const count = n * rows;
  const uvs = new Float32Array(count * 2);
  let dist = 0;

  for (let i = 0; i < n; i++) {
    if (i > 0) {
      dist += Math.hypot(points[i].x - points[i - 1].x, points[i].z - points[i - 1].z);
    }
    const u = dist / bandWorldLength;
    for (let k = 0; k < rows; k++) {
      const idx = i * rows + k;
      uvs[idx * 2] = u;
      uvs[idx * 2 + 1] = k / (rows - 1);
    }
  }

  const indices = [];
  for (let i = 0; i < n - 1; i++) {
    for (let k = 0; k < rows - 1; k++) {
      const a = i * rows + k;
      const b = a + 1;
      const c = (i + 1) * rows + k;
      const d = c + 1;
      indices.push(a, c, b, b, c, d);
    }
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.BufferAttribute(new Float32Array(count * 3), 3));
  geometry.setAttribute("uv", new THREE.BufferAttribute(uvs, 2));
  geometry.setIndex(indices);

  const ribbon = { geometry, points, normals, rows, rawH: new Float32Array(count) };
  applyRiverWidth(ribbon, halfWidth, drape);
  return ribbon;
}

function applyRiverExaggeration(geometry, rawH, exaggeration) {
  const pos = geometry.attributes.position;
  const offsetRaw = 3;
  for (let i = 0; i < rawH.length; i++) {
    pos.setY(i, (rawH[i] + offsetRaw) * exaggeration);
  }
  pos.needsUpdate = true;
}

function distanceToSegment2D(px, pz, ax, az, bx, bz) {
  const dx = bx - ax;
  const dz = bz - az;
  const lenSq = dx * dx + dz * dz || 1;
  let t = ((px - ax) * dx + (pz - az) * dz) / lenSq;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(px - (ax + t * dx), pz - (az + t * dz));
}

function findHoveredRiver(riverRibbons, x, z, worldDiag) {
  let best = null;
  let bestDist = Infinity;
  for (const ribbon of riverRibbons) {
    const tolerance = Math.max(ribbon.currentHalfWidth * 2.5, worldDiag * 0.0012);
    const pts = ribbon.points;
    for (let i = 0; i < pts.length - 1; i++) {
      const d = distanceToSegment2D(x, z, pts[i].x, pts[i].z, pts[i + 1].x, pts[i + 1].z);
      if (d < bestDist && d <= tolerance) {
        bestDist = d;
        best = ribbon.river;
      }
    }
  }
  return best;
}

// --- Zones d'interet (points_of_interest) : marqueur "tactique" (repere au
// sol + balise verticale + etiquette hexagonale rapprochee), pense pour
// rester lisible meme au fond d'une vallee — contrairement a un simple halo
// plat au sol (quasi invisible vu de biais depuis une camera aerienne), la
// balise verticale + l'etiquette proche du sommet restent visibles quel que
// soit le relief environnant. `point.position` est deja en coordonnees
// locales de la scene (capture par raycast contre le terrain au moment du
// placement, cf. handlePointerDown), donc directement utilisable sans
// reechantillonnage.
// Bleu tres clair de la charte : les villes occupent deja l'orange, le bleu
// moyen et le sable (cf. CITY_TIERS), celui-ci reste disponible et se
// detache bien du relief vert/ocre.
const POI_ACCENT_COLOR = "#b7e0e1";

// Pastille numerotee au sommet de la balise : meme famille que les reperes de
// ville, en plus clair. (Elle a d'abord ete dessinee en hexagone cercle d'un
// reticule anime, trop connote jeu video pour cet atelier.)
function createPoiLabelSprite(text, accentColor) {
  const canvas = document.createElement("canvas");
  canvas.width = 220;
  canvas.height = 220;
  const ctx = canvas.getContext("2d");
  const cx = canvas.width / 2;
  const cy = canvas.height / 2;
  const r = 86;

  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fillStyle = "rgba(10, 26, 36, 0.9)";
  ctx.fill();
  ctx.lineWidth = 7;
  ctx.strokeStyle = accentColor;
  ctx.stroke();

  ctx.fillStyle = "#f4f8f9";
  ctx.font = "800 96px 'GTWalsheimPro-Condensed', sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(text, cx, cy + 6);

  const tex = new THREE.CanvasTexture(canvas);
  const mat = new THREE.SpriteMaterial({ map: tex, transparent: true, depthWrite: false });
  return new THREE.Sprite(mat);
}

// `index` a null = repere SEUL : pas d'etiquette numerotee. Un « 1 » ne dit
// quelque chose que s'il y a un « 2 » ailleurs ; sur une page localisee, qui
// n'a qu'un lieu, il ne fait qu'encombrer le relief.
function createPoiMarker(point, index, worldDiag) {
  const group = new THREE.Group();
  group.position.set(point.position.x, point.position.y, point.position.z);

  const baseRadius = Math.max(worldDiag * 0.0032, 200);
  // Hauteur fixe (pas liee a zRange comme les faisceaux des villes) : reste
  // courte et constante quel que soit le relief, donc l'etiquette est
  // toujours "proche" du sol comme demande — jamais une tour qui domine le
  // paysage sur un territoire tres montagneux.
  const beaconHeight = Math.max(worldDiag * 0.016, 950);

  // --- repere au sol : pastille pleine + anneau large, comme les villes ---
  const diamondGeo = new THREE.CircleGeometry(baseRadius * 0.8, 24);
  const diamondMat = new THREE.MeshBasicMaterial({
    color: POI_ACCENT_COLOR,
    transparent: true,
    opacity: 0.85,
    side: THREE.DoubleSide,
    depthWrite: false,
  });
  const diamond = new THREE.Mesh(diamondGeo, diamondMat);
  diamond.rotation.x = -Math.PI / 2;
  diamond.position.y = 5;
  group.add(diamond);

  const lockRingGeo = new THREE.RingGeometry(baseRadius * 1.7, baseRadius * 1.95, 32);
  const lockRingMat = new THREE.MeshBasicMaterial({
    color: POI_ACCENT_COLOR,
    transparent: true,
    opacity: 0.55,
    side: THREE.DoubleSide,
    depthWrite: false,
  });
  const lockRing = new THREE.Mesh(lockRingGeo, lockRingMat);
  lockRing.rotation.x = -Math.PI / 2;
  lockRing.position.y = 6;
  group.add(lockRing);

  // --- balise verticale : trait fin reliant le repere au sol a l'etiquette
  // (le "trait" demande — plus court/plus proche que les faisceaux des
  // villes, et toujours vertical donc lisible sous n'importe quel angle de
  // camera, contrairement a un lien diagonal en espace ecran) ---
  const beaconGeo = new THREE.BufferGeometry().setFromPoints([
    new THREE.Vector3(0, 6, 0),
    new THREE.Vector3(0, beaconHeight, 0),
  ]);
  const beaconMat = new THREE.LineBasicMaterial({ color: POI_ACCENT_COLOR, transparent: true, opacity: 0.85 });
  const beacon = new THREE.Line(beaconGeo, beaconMat);
  group.add(beacon);

  // --- etiquette numerotee, hexagonale, au sommet de la balise ---
  const labelScale = Math.max(worldDiag * 0.02, 1200);
  let label = null;
  if (index != null) {
    label = createPoiLabelSprite(String(index + 1), POI_ACCENT_COLOR);
    label.scale.set(labelScale, labelScale, 1);
    label.position.y = beaconHeight + labelScale * 0.35;
    group.add(label);
  }

  // --- zone de clic : englobe repere + balise + etiquette ---
  const hitHeight = beaconHeight + labelScale;
  const hitGeo = new THREE.CylinderGeometry(baseRadius * 2.2, baseRadius * 2.2, hitHeight, 10);
  const hitMesh = new THREE.Mesh(hitGeo, new THREE.MeshBasicMaterial({ visible: false }));
  hitMesh.position.y = hitHeight / 2;
  group.add(hitMesh);

  // Le connecteur du panneau s'accroche a l'etiquette quand elle existe, sinon
  // au sommet de la balise : un repere seul n'est pas numerote, mais il doit
  // rester reliable a sa fiche.
  const anchor = label || beacon;
  const anchorRadius = label ? labelScale : 0;

  return { group, diamond, lockRing, beacon, label, anchor, anchorRadius, hitMesh, data: point };
}

// --- Panneau POI ancre a l'ecran : positionne le panneau HTML + le
// connecteur (coude orthogonal, façon schema technique) pres de la
// projection ecran du marqueur, en basculant a gauche si le panneau
// deborderait a droite. Pure manipulation DOM (pas de re-render React),
// appelee a chaque frame depuis animate() pour suivre l'orbite/zoom.
const POI_PANEL_WIDTH = 300;
const POI_PANEL_RISE = 46;
const POI_PANEL_GAP = 24;
const POI_PANEL_MARGIN = 12;
const POI_ANCHOR_CLEARANCE = 5; // ecart entre le bord de la pastille et l'ancre

// `badgeRadiusPx` = demi-hauteur de la pastille numerotee a l'ecran. Le trait
// part du BORD SUPERIEUR de la pastille, pas de son centre : ancre au centre,
// le rond d'accroche recouvrait le numero et le rendait illisible.
function updatePoiPanelPosition(panelEl, lineEl, sx, sy, badgeRadiusPx, containerWidth, containerHeight) {
  const anchorY = sy - badgeRadiusPx - POI_ANCHOR_CLEARANCE;
  const goLeft = sx + POI_PANEL_GAP + POI_PANEL_WIDTH + POI_PANEL_MARGIN > containerWidth;
  const elbowY = Math.max(POI_PANEL_MARGIN, anchorY - POI_PANEL_RISE);
  let panelLeft = goLeft ? sx - POI_PANEL_GAP - POI_PANEL_WIDTH : sx + POI_PANEL_GAP;
  panelLeft = Math.min(Math.max(panelLeft, POI_PANEL_MARGIN), containerWidth - POI_PANEL_WIDTH - POI_PANEL_MARGIN);
  const panelTop = Math.min(Math.max(elbowY - 12, POI_PANEL_MARGIN), Math.max(POI_PANEL_MARGIN, containerHeight - 80));

  panelEl.style.left = `${panelLeft}px`;
  panelEl.style.top = `${panelTop}px`;
  panelEl.style.visibility = "visible";

  // Coude vertical puis horizontal, prolonge d'un talon qui rentre sous le
  // panneau : la liaison se lit comme un branchement, pas comme une fleche.
  const elbowX = goLeft ? panelLeft + POI_PANEL_WIDTH : panelLeft;
  const stubX = goLeft ? elbowX - 14 : elbowX + 14;
  if (lineEl) {
    lineEl.setAttribute("points", `${sx},${anchorY} ${sx},${elbowY} ${stubX},${elbowY}`);
  }
}

function hidePoiPanel(panelEl, lineEl) {
  if (panelEl) panelEl.style.visibility = "hidden";
  if (lineEl) lineEl.setAttribute("points", "");
}

function disposePoiMarker(m) {
  m.diamond.geometry.dispose();
  m.diamond.material.dispose();
  m.lockRing.geometry.dispose();
  m.lockRing.material.dispose();
  m.beacon.geometry.dispose();
  m.beacon.material.dispose();
  m.hitMesh.geometry.dispose();
  m.hitMesh.material.dispose();
  // Absente sur un repere seul, qui n'est pas numerote.
  if (m.label) {
    m.label.material.map?.dispose();
    m.label.material.dispose();
  }
}

// Teintes de categorie pour les usages de prelevement : assez distinctes pour
// se lire sur un relief vert/ocre, mais rabattues sur des tons naturels — la
// premiere version, fluo (magenta, vert acide, cyan), piquait l'oeil et
// sortait completement de la palette de l'atelier.
const USAGE_PALETTE = [
  "#e2694f", "#5aa9c0", "#e0ac3c", "#6faa68", "#a173ad", "#3f77b0", "#c4636e", "#8ba84a",
];

/**
 * CATALOGUE : ce que le datapack charge propose reellement aux controles du
 * registre (layerSpecs.js). C'est le pont entre "les donnees se decrivent
 * elles-memes" et "les reglages de rendu vivent dans le front" — un controle
 * dit `options: "communes.indicators"` et se retrouve peuple par les
 * specifications que le pipeline a calculees sur les donnees.
 *
 * Il sert aussi a borner les curseurs sur du reel : le seuil de population
 * des villes plafonne a la plus grosse ville du territoire, pas a un ordre de
 * grandeur devine a l'ecriture du code.
 */
function buildCatalog({ communesData, geologyData, climateAxes, dischargeAxes, cities, riverLengths, usages }) {
  const positiveLengths = (riverLengths || []).filter((l) => l > 0);
  return {
    rivers: {
      lengthMin: positiveLengths.length ? Math.min(...positiveLengths) : 1,
      lengthMax: riverLengths?.length ? Math.max(...riverLengths) : 1,
    },
    cities: cityCatalog(cities),
    communes: {
      indicators: communesData?.indicators || [],
      defaultIndicator: communesData?.default_indicator || null,
    },
    geology: {
      indicators: geologyData?.indicators || [],
      defaultIndicator: geologyData?.default_indicator || null,
    },
    climate: {
      variables: climateAxes?.variables || [],
      horizons: (climateAxes?.horizons || []).map((h) => ({ id: h, label: horizonLabel(h) })),
      // Les periodes sont un axe du datapack, mais leurs libelles restent une
      // affaire de front (climateLayer.js) : "m07" ne se montre pas tel quel.
      periods: (climateAxes?.periods || CLIMATE_PERIODS).map((p) => ({ id: p, label: periodLabel(p) })),
    },
    discharge: {
      metrics: dischargeAxes?.metrics || [],
      horizons: (dischargeAxes?.horizons || []).map((h) => ({ id: h, label: horizonLabel(h) })),
    },
    withdrawals: {
      usages: (usages || []).map((usage) => ({ id: usage, label: usageShortLabel(usage) })),
    },
  };
}

// Libelle d'usage BNPE pour la legende : les intitules officiels sont longs
// ("INDUSTRIE et ACTIVITES ECONOMIQUES (hors irrigation, hors energie)") et
// passaient sur trois lignes, ecrasant le reste de la legende. On n'affiche
// que la partie avant la parenthese — l'intitule complet reste accessible en
// infobulle (attribut title) plutot que perdu.
function usageShortLabel(usage) {
  const cut = usage.indexOf("(");
  return (cut > 0 ? usage.slice(0, cut) : usage).trim();
}

function formatVolume(v) {
  if (v == null) return "n/d";
  if (v >= 1e6) return `${(v / 1e6).toFixed(1)} Mm³`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(0)} k m³`;
  return `${v.toFixed(0)} m³`;
}

function applyWithdrawalTransform(mesh, dummy, meta, exaggeration, radius) {
  meta.currentY = meta.rawH * exaggeration;
  const height = meta.hiddenByFilter ? 0 : meta.baseHeight;
  const r = meta.hiddenByFilter ? 0 : radius;
  dummy.position.set(meta.x, meta.currentY + height / 2, meta.z);
  dummy.scale.set(r, height, r);
  dummy.updateMatrix();
  mesh.setMatrixAt(meta.index, dummy.matrix);
}

// `hiddenUsages` (et non plus la liste des usages ACTIFS) : les reglages de
// vue expriment tous une exclusion, ce qui a l'avantage de faire du "rien de
// masque" l'etat par defaut — un tableau vide, qui ne pese rien dans la vue
// enregistree et reste juste si le territoire gagne un usage de plus.
function recomputeWithdrawalScales(withdrawalMeta, hiddenUsages, worldDiag, zRange, widthScale, heightScale) {
  const hidden = hiddenUsages instanceof Set ? hiddenUsages : new Set(hiddenUsages || []);
  const visible = withdrawalMeta.filter((m) => !hidden.has(m.data.usage));
  const sortedVolumes = visible.map((m) => m.data.total_volume || 0).sort((a, b) => a - b);
  const domainMax = sortedVolumes.length
    ? sortedVolumes[Math.floor(0.9 * (sortedVolumes.length - 1))] || 1
    : 1;

  const minHeight = Math.max(worldDiag * 0.0009, 90) * heightScale;
  const maxHeight = Math.max(zRange * 0.55, 550) * heightScale;
  const barRadius = Math.max(worldDiag * 0.0011, 140) * widthScale;
  const hitRadius = Math.max(barRadius * 3, worldDiag * 0.001);

  withdrawalMeta.forEach((meta) => {
    const t = Math.sqrt(Math.min(meta.data.total_volume || 0, domainMax) / domainMax);
    meta.baseHeight = minHeight + (maxHeight - minHeight) * t;
    meta.baseRadius = barRadius;
    meta.hitRadius = hitRadius;
  });
}

function buildWallPositions(wallEdges, exaggeration, socleDepthRaw) {
  const positions = new Float32Array(wallEdges.length * 18);
  const baseY = -socleDepthRaw * exaggeration;
  for (let i = 0; i < wallEdges.length; i++) {
    const e = wallEdges[i];
    const ay = e.rawHa * exaggeration;
    const by = e.rawHb * exaggeration;
    const o = i * 18;
    positions[o] = e.ax; positions[o + 1] = ay; positions[o + 2] = e.az;
    positions[o + 3] = e.bx; positions[o + 4] = by; positions[o + 5] = e.bz;
    positions[o + 6] = e.ax; positions[o + 7] = baseY; positions[o + 8] = e.az;
    positions[o + 9] = e.bx; positions[o + 10] = by; positions[o + 11] = e.bz;
    positions[o + 12] = e.bx; positions[o + 13] = baseY; positions[o + 14] = e.bz;
    positions[o + 15] = e.ax; positions[o + 16] = baseY; positions[o + 17] = e.az;
  }
  return positions;
}

/**
 * Legende d'un indicateur, quelle que soit sa couche (communes, geologie,
 * debit) et quelle que soit sa nature.
 *
 * Un seul composant pour les quatre `kind` : c'est la contrepartie directe du
 * moteur descriptif — la legende se deduit de la specification, jamais des
 * couleurs affichees a l'ecran, donc les deux ne peuvent pas diverger.
 */
function IndicatorLegend({ indicator, legend, note }) {
  if (!indicator || !legend) return null;
  return (
    <>
      {legend.kind === "quantitative" ? (
        <>
          <div className="terrain3d-legend-gradient" style={{ background: legend.gradient }} />
          <div className="terrain3d-legend-labels">
            <span>{formatNumber(legend.min, legend.format)} {legend.unit}</span>
            {/* Une echelle divergente doit dire ou tombe son point neutre,
                sinon le milieu de la rampe passe pour une mediane. */}
            {legend.neutral != null && <span>0</span>}
            <span>{formatNumber(legend.max, legend.format)} {legend.unit}</span>
          </div>
          {legend.scale === "log" && (
            <p className="terrain3d-sectionnote">Échelle logarithmique</p>
          )}
        </>
      ) : (
        <div className="terrain3d-chiplist">
          {legend.entries.map(({ value, label, color, count }) => (
            <span key={String(value)} className="terrain3d-chip" style={{ "--chip-color": color }}>
              <span className="terrain3d-usage-dot" />
              <span className="terrain3d-chip-label" title={label}>{label}</span>
              {count != null && <span className="terrain3d-chip-count">{count}</span>}
            </span>
          ))}
        </div>
      )}
      {note && <p className="terrain3d-sectionnote">{note}</p>}
      {indicator.source && <p className="terrain3d-sectionnote">{indicator.source}</p>}
    </>
  );
}

/**
 * Fiche d'une couche, ouverte a la demande par le bouton ⓘ.
 *
 * SON DESTINATAIRE EST L'ANIMATEUR, pas le joueur : c'est ce qu'il repond
 * quand quelqu'un demande "ca vient d'ou, ce chiffre ?" en pleine seance. Le
 * contenu vient du datapack (territories.json, alimente par build_manifest.py)
 * et jamais d'un texte ecrit ici — sinon les mises en garde divergeraient des
 * donnees qu'elles decrivent.
 *
 * Les `caveats` sont la partie la plus importante : ce sont les limites qui
 * vivaient jusqu'ici dans le README du pipeline, c'est-a-dire nulle part pour
 * qui anime un atelier. Une limite de donnee qu'on ne lit qu'en ouvrant le
 * code source n'existe pas.
 */
function LayerInfo({ layerId, info }) {
  if (!info) return null;
  return (
    <div className="terrain3d-layerinfo">
      <span className="terrain3d-eyebrow">{layerLabel(layerId)}</span>
      {info.description && <p className="terrain3d-layerinfo-text">{info.description}</p>}
      {info.source && <p className="terrain3d-sectionnote">Source : {info.source}</p>}
      {(info.caveats || []).map((caveat) => (
        <p key={caveat} className="terrain3d-layerinfo-caveat">{caveat}</p>
      ))}

    </div>
  );
}

const TerrainViewer = forwardRef(function TerrainViewer(
  {
    territoryId, availableLayers = [], activeLayers = [], layerSettings = null,
    cameraPose = null, animateCamera = true, fogEnabled = true,
    points = [], placementMode = false, onTerrainClick, contentInsets = null,
    onCatalogChange = null, onSelectionChange = null, uiScale = 1, legendsOpen = true,
    // --- Reglages d'ECRAN, poses par l'ecran de territoire (TerritoryStage.js)
    // selon que la carte est au premier plan ou en fond de page. ---
    paused = false,      // scene montee mais plus regardee : rendu gele
    // "full" : tout l'habillage (legendes, reglages, fiches de couche).
    // "markers" : seulement les marqueurs et leur fiche — une page localisee
    //             montre un lieu, pas un outil de consultation.
    // "none" : le relief nu, en fond d'une page de jeu.
    chrome = "full",
    openPointId = null,  // marqueur dont la fiche s'ouvre d'elle-meme
    autoRotate = null,   // vitesse imposee ; null = interrupteur de l'animateur
    maxFps = null,       // bridage du rendu ; null = frequence de l'ecran
  },
  ref
) {
  const mountRef = useRef(null);
  const stateRef = useRef({});
  const [status, setStatus] = useState("loading"); // loading | error | ready
  const [meta, setMeta] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [selectedWithdrawal, setSelectedWithdrawal] = useState(null);
  const [selectedPoi, setSelectedPoi] = useState(null);
  const selectedPoiRef = useRef(selectedPoi);
  selectedPoiRef.current = selectedPoi;
  // Zone d'intérêt montrée en grand par-dessus la carte. Un état à part de
  // `selectedPoi` : fermer l'agrandissement doit rendre la fiche ancrée au
  // marqueur, pas la refermer elle aussi.
  const [zoomedPoi, setZoomedPoi] = useState(null);
  // DOM refs mis a jour de façon imperative a chaque frame (animate()) pour
  // suivre la position ecran du marqueur pendant l'orbite/zoom — pas via
  // React state, qui re-rendrait le composant 60x/s pour rien.
  const poiPanelElRef = useRef(null);
  const poiLineElRef = useRef(null);
  const [focusedOuvrageIndex, setFocusedOuvrageIndex] = useState(0);
  const [usageList, setUsageList] = useState([]);
  const [geologyIndicators, setGeologyIndicators] = useState([]); // specifications lues dans geology.json
  const [hoveredGeology, setHoveredGeology] = useState(null); // { entity, clientX, clientY }
  const [hoveredRiver, setHoveredRiver] = useState(null);
  // Communes : les donnees (communes.json) et l'indicateur affiche. Un seul
  // raster charge, l'indicateur ne fait que changer les couleurs.
  const [communes, setCommunes] = useState([]);
  const [communeIndicators, setCommuneIndicators] = useState([]);
  // Debit : une station par cours d'eau instrumente, pas de nappe interpolee
  // (un debit ne se lit qu'au point ou il est mesure, cf. dischargeLayer.js).
  const [dischargeStations, setDischargeStations] = useState([]);
  const [dischargeAxes, setDischargeAxes] = useState({ horizons: [], metrics: [] });
  const [dischargeNormalized, setDischargeNormalized] = useState(false);
  const [selectedDischarge, setSelectedDischarge] = useState(null);
  const [hoveredCommune, setHoveredCommune] = useState(null); // { commune, clientX, clientY }
  const [selectedCommune, setSelectedCommune] = useState(null);
  // Climat : points de mesure + axes (variables/horizons/periodes) decouverts
  // dans le fichier.
  const [climatePoints, setClimatePoints] = useState([]);
  const [climateAxes, setClimateAxes] = useState({ variables: [], horizons: [], periods: [] });
  const [selectedStation, setSelectedStation] = useState(null);
  const [cityList, setCityList] = useState([]);
  // Descriptions editoriales des couches (territories.json) : source,
  // methode, mises en garde. Charge separement des donnees parce que ce n'en
  // est pas — c'est ce que l'animateur dit A PROPOS d'elles.
  const [layerDocs, setLayerDocs] = useState({});
  const layerInfo = (id) => layerDocs[id] || null;

  // --- REGLAGES DE COUCHES : trois etages, du plus general au plus precis ---
  //
  // 1. les defauts du registre (layerSpecs.js), resolus contre le CATALOGUE,
  //    c'est-a-dire ce que le datapack charge propose reellement (indicateurs
  //    disponibles, horizons, bornes de population...) ;
  // 2. `layerSettings`, la vue enregistree par l'auteur — le viewer est
  //    CONTROLE sur ce point : il affiche ce qu'on lui passe, il ne decide
  //    plus ;
  // 3. `animatorSettings`, ce que l'animateur bricole en seance pour repondre
  //    a une question. Ephemere par construction : remis a plat des que
  //    l'etape change, donc impossible d'abimer la vue de l'auteur.
  //
  // Ce sont ces memes trois etages qui rendent la capture inutile : le
  // builder n'a pas a interroger le viewer sur son etat, il le lui dicte.
  const [catalog, setCatalog] = useState({});
  const [animatorSettings, setAnimatorSettings] = useState({});
  const authoredSettingsKey = JSON.stringify(layerSettings || {});

  const settings = useMemo(
    () => mergeLayerSettings(defaultLayerSettings(catalog), layerSettings, animatorSettings),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [catalog, authoredSettingsKey, animatorSettings]
  );

  const setLayerSetting = (layerId, controlId, value) => {
    setAnimatorSettings((prev) => ({
      ...prev,
      [layerId]: { ...(prev[layerId] || {}), [controlId]: value },
    }));
  };

  // Lu par la construction de scene (asynchrone) : si les reglages ont deja
  // change pendant le chargement, les nappes naissent a la bonne valeur
  // plutot que de sauter au premier rendu.
  const settingsRef = useRef(settings);
  settingsRef.current = settings;

  // Alias de lecture. Ils gardent les noms d'avant la centralisation, ce qui
  // laisse le rendu et les effets ci-dessous parfaitement lisibles : seul le
  // LIEU ou la valeur est rangee a change, pas ce qu'elle veut dire.
  const communeIndicatorId = settings.communes?.indicator ?? null;
  const communeOpacity = settings.communes?.opacity ?? 0.55;
  const communeBordersOn = settings.communes?.borders !== false;
  const geologyIndicatorId = settings.geology?.indicator ?? null;
  const geologyOpacity = settings.geology?.opacity ?? 0.8;
  const climateVariableId = settings.climate?.variable ?? null;
  const climateHorizon = settings.climate?.horizon ?? null;
  const climatePeriod = settings.climate?.period ?? "annuel";
  const climateOpacity = settings.climate?.opacity ?? 0.55;
  const climateStationsOn = settings.climate?.stations !== false;
  const climateSmoothingKm = settings.climate?.smoothing_km ?? CLIMATE_DEFAULT_SMOOTHING_M / 1000;
  const dischargeMetricId = settings.discharge?.metric ?? null;
  const dischargeHorizon = settings.discharge?.horizon ?? null;
  const riverWidthScale = settings.rivers?.width_scale ?? 1;
  const riverLengthCapKm = settings.rivers?.length_cap_km ?? null;
  const barWidthScale = settings.withdrawals?.bar_width ?? 1;
  const barHeightScale = settings.withdrawals?.bar_height ?? 1.35;
  const hiddenUsages = settings.withdrawals?.usages || [];

  // Mirroirs a jour a chaque rendu (pas de useEffect) : lus par la
  // construction de scene (async, dans le useEffect de montage ci-dessous)
  // pour recuperer la DERNIERE valeur au moment ou la scene se construit,
  // sans pour autant declencher une reconstruction quand ces props changent
  // seules (voir les effets dedies plus bas pour la mise a jour post-montage).
  const cameraPoseRef = useRef(cameraPose);
  cameraPoseRef.current = cameraPose;
  const activeLayersRef = useRef(activeLayers);
  activeLayersRef.current = activeLayers;
  // Reglages de l'auteur, lus par initScene : le catalogue n'existe qu'une
  // fois les fichiers charges, donc les defauts ne peuvent etre resolus qu'a
  // l'interieur de la construction de scene (voir `initialSettings`).
  const authoredSettingsRef = useRef(layerSettings);
  authoredSettingsRef.current = layerSettings;
  const onCatalogChangeRef = useRef(onCatalogChange);
  onCatalogChangeRef.current = onCatalogChange;
  const fogEnabledRef = useRef(fogEnabled);
  fogEnabledRef.current = fogEnabled;
  // placementMode/onTerrainClick sont lus depuis handlePointerDown, cree une
  // seule fois a la construction de la scene : un ref (mis a jour a chaque
  // rendu) evite de capturer une valeur perimee dans cette closure.
  const placementModeRef = useRef(placementMode);
  placementModeRef.current = placementMode;
  const onTerrainClickRef = useRef(onTerrainClick);
  onTerrainClickRef.current = onTerrainClick;
  const pointsRef = useRef(points);
  pointsRef.current = points;
  // Zones du canvas masquees par l'UI du parent (panneau d'etape...). Lu a
  // chaque frame par animate() pour recentrer le terrain dans la zone visible
  // — un ref plutot qu'un state : la valeur change avec le mode de panneau,
  // pas 60x/s, et animate() doit toujours voir la derniere.
  const contentInsetsRef = useRef(contentInsets);
  contentInsetsRef.current = contentInsets;

  // Etat purement local d'affichage (repli des panneaux, brume, rotation) :
  // ni des donnees, ni des reglages de couche — il n'a donc rien a faire dans
  // la pile de reglages ci-dessus, que l'auteur enregistre.
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const [autoRotateOn, setAutoRotateOn] = useState(false);
  // Les legendes sont deployees pour le joueur (c'est la qu'il lit ce que la
  // couleur raconte) mais repliees dans le builder : sur un canevas d'auteur,
  // le panneau mangeait un cinquieme de la carte alors que les memes
  // selecteurs sont dans l'inspecteur, a cote.
  const [legendsVisible, setLegendsVisible] = useState(legendsOpen);
  const [layerMenuOpen, setLayerMenuOpen] = useState(false);
  const [openInfoLayer, setOpenInfoLayer] = useState(null); // couche dont la fiche ⓘ est ouverte
  // L'exageration verticale reste fixe : les zones d'interet sont enregistrees
  // par l'auteur en coordonnees de scene, calculees a x2,5 — la faire varier
  // decrocherait les marqueurs du relief.
  const [fogOn, setFogOn] = useState(fogEnabled);
  // Couches allumees/eteintes par l'animateur en plus de celles prevues par
  // l'auteur pour l'etape : { [layerId]: true|false }, remis a plat a chaque
  // changement d'etape — meme logique ephemere que `animatorSettings`.
  const [layerOverrides, setLayerOverrides] = useState({});
  // Rappel des commandes : utile a l'arrivee, parasite ensuite. S'efface des
  // que le joueur a manipule la carte, ou au bout de quelques secondes.
  const [hintDismissed, setHintDismissed] = useState(false);
  const autoRotateOnRef = useRef(autoRotateOn);
  autoRotateOnRef.current = autoRotateOn;
  // GEL DU RENDU. La scène peut rester montée sans être regardée — c'est même
  // tout l'objet de l'ecran de territoire (cf. TerritoryStage.js), qui la garde
  // construite entre deux pages. Continuer a dessiner 60 images par seconde
  // sous une page de mosaique disputerait le fil principal a React pour une
  // carte que personne ne voit. Par un ref et non par une dependance d'effet :
  // la boucle d'animation est creee une fois, dans l'effet de construction.
  const pausedRef = useRef(paused);
  pausedRef.current = paused;
  const maxFpsRef = useRef(maxFps);
  maxFpsRef.current = maxFps;
  const forcedRotateRef = useRef(autoRotate);
  forcedRotateRef.current = autoRotate;

  const availableLayersKey = (availableLayers || []).slice().sort().join(",");
  const activeLayersKey = (activeLayers || []).slice().sort().join(",");
  const cameraPoseKey = cameraPose
    ? `${cameraPose.azimuth}|${cameraPose.elevation}|${cameraPose.distance}|${cameraPose.target?.x}|${cameraPose.target?.y}|${cameraPose.target?.z}`
    : "";
  const pointsKey = JSON.stringify(points || []);

  useImperativeHandle(ref, () => ({
    getCurrentPose: () => {
      const s = stateRef.current;
      if (!s.camera || !s.controls) return null;
      return readPoseFromScene(s.camera, s.controls);
    },
    applyPose: (pose, { animate = false } = {}) => {
      const s = stateRef.current;
      if (!s.camera || !s.controls || !pose) return;
      const from = animate ? readPoseFromScene(s.camera, s.controls) : null;
      if (!from) {
        applyPoseToScene(s.camera, s.controls, pose);
        return;
      }
      s.poseTween = {
        from,
        to: { ...pose, target: pose.target || { x: 0, y: 0, z: 0 } },
        startedAt: performance.now(),
        duration: POSE_TRANSITION_MS,
      };
    },
    /**
     * Cadrage d'ensemble : la pose par defaut, calculee sur la diagonale du
     * territoire. C'est le point de repere dont un auteur a besoin apres avoir
     * orbite trop loin — sans lui, retrouver une vue d'ensemble se fait a la
     * molette, au jugé.
     */
    fitTerritory: ({ animate = true } = {}) => {
      const s = stateRef.current;
      if (!s.camera || !s.controls || !s.worldDiag) return;
      const pose = {
        azimuth: DEFAULT_CAMERA_AZIMUTH,
        elevation: DEFAULT_CAMERA_ELEVATION,
        distance: s.worldDiag * DEFAULT_CAMERA_DISTANCE_RATIO,
        target: { x: 0, y: 0, z: 0 },
      };
      const from = animate ? readPoseFromScene(s.camera, s.controls) : null;
      if (!from) {
        applyPoseToScene(s.camera, s.controls, pose);
        return;
      }
      s.poseTween = { from, to: pose, startedAt: performance.now(), duration: POSE_TRANSITION_MS };
    },
    /**
     * Reglages effectivement appliques a la scene. Le builder n'en a plus
     * besoin pour composer une vue (il DICTE les reglages via `layerSettings`
     * plutot que de les relire), mais c'est ce qui permet a un appelant de
     * repartir de ce que l'animateur a bricole en seance.
     */
    getLayerSettings: () => settingsRef.current,
    getCatalog: () => catalog,
  }), [catalog]);

  // --- Ce qui est ouvert sur la carte, remonte a l'ecran qui porte le viewer.
  // Un graphique d'etape peut se regler sur « suivre la selection » : cliquer
  // une station de debit redessine alors sa courbe, ce qui est le geste
  // d'atelier qu'aucune capture d'ecran ne remplace. Les fiches restent
  // maitresses de leur propre affichage — on ne fait que signaler.
  //
  // La fonction passe par une ref : sans elle, un appelant qui donne une
  // lambda en ligne relancerait l'effet a chaque rendu, donc remonterait une
  // selection, donc provoquerait un rendu — une boucle. L'effet ne doit
  // dependre que de ce qui est selectionne. ---
  const onSelectionChangeRef = useRef(onSelectionChange);
  onSelectionChangeRef.current = onSelectionChange;
  useEffect(() => {
    onSelectionChangeRef.current?.({
      discharge: selectedDischarge,
      commune: selectedCommune,
      climatePoint: selectedStation,
    });
  }, [selectedDischarge, selectedCommune, selectedStation]);

  // --- Fiches des couches : quelques Ko, independants de la scene. Un echec
  // de chargement laisse simplement les boutons ⓘ absents, il ne doit jamais
  // empecher la carte de s'afficher. ---
  useEffect(() => {
    let cancelled = false;
    fetch(TERRITORIES_URL)
      .then((r) => (r.ok ? r.json() : { territories: [] }))
      .then((data) => {
        if (cancelled) return;
        const territory = (data.territories || []).find((t) => t.id === territoryId);
        setLayerDocs(
          Object.fromEntries((territory?.layer_catalog || []).map((layer) => [layer.id, layer]))
        );
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [territoryId]);

  // --- Chargement des donnees + init three.js. Reconstruit uniquement si le
  // territoire ou l'ensemble des calques a recuperer change — pas a chaque
  // changement de cameraPose/activeLayers (geres par des effets legers). ---
  useEffect(() => {
    let cancelled = false;
    const dataUrl = territoryUrl(territoryId);
    const avail = new Set(availableLayers);

    async function load() {
      try {
        const [metaRes, binRes] = await Promise.all([
          fetch(`${dataUrl}/meta.json`),
          fetch(`${dataUrl}/heightmap.bin`),
        ]);
        if (!metaRes.ok || !binRes.ok) throw new Error("fichiers introuvables");
        const metaJson = await metaRes.json();
        const buffer = await binRes.arrayBuffer();
        if (cancelled) return;
        const heights = new Float32Array(buffer);

        const riversData = avail.has("rivers")
          ? await fetch(`${dataUrl}/rivers.json`).then((r) => (r.ok ? r.json() : [])).catch(() => [])
          : [];
        const citiesData = avail.has("cities")
          ? await fetch(`${dataUrl}/cities.json`).then((r) => (r.ok ? r.json() : [])).catch(() => [])
          : [];
        // Les quatre jeux de donnees ci-dessous ont un second lecteur : les
        // graphiques d'etape (cf. datapack.js). Ils passent par le cache
        // partage, pour n'etre telecharges qu'une fois par seance quel que
        // soit celui qui les demande en premier.
        const withdrawalsData = avail.has("withdrawals")
          ? await loadDataset(territoryId, "withdrawals").catch(() => [])
          : [];
        // Uint16 depuis la refonte : le raster geologique porte un index
        // d'ENTITE BD LISA (et non plus une classe de couleur sur un octet),
        // exactement comme celui des communes.
        const geologyCodes = avail.has("geology")
          ? await fetch(`${dataUrl}/geology.bin`)
              .then((r) => (r.ok ? r.arrayBuffer() : null))
              .then((buf) => (buf ? new Uint16Array(buf) : null))
              .catch(() => null)
          : null;
        const geologyData = avail.has("geology")
          ? await fetch(`${dataUrl}/geology.json`).then((r) => (r.ok ? r.json() : null)).catch(() => null)
          : null;
        const communeCodes = avail.has("communes")
          ? await fetch(`${dataUrl}/communes.bin`)
              .then((r) => (r.ok ? r.arrayBuffer() : null))
              .then((buf) => (buf ? new Uint16Array(buf) : null))
              .catch(() => null)
          : null;
        const communesData = avail.has("communes")
          ? await loadDataset(territoryId, "communes").catch(() => null)
          : null;
        const climateData = avail.has("climate")
          ? await loadDataset(territoryId, "climate").catch(() => null)
          : null;
        const dischargeData = avail.has("discharge")
          ? await loadDataset(territoryId, "discharge").catch(() => null)
          : null;
        if (cancelled) return;

        setMeta(metaJson);
        initScene(metaJson, heights, {
          riversData, citiesData, withdrawalsData,
          geologyCodes, geologyData, communeCodes, communesData,
          climateData, dischargeData,
        });
        setStatus("ready");
      } catch (e) {
        console.error("TerrainViewer: echec chargement", e);
        if (!cancelled) setStatus("error");
      }
    }

    function initScene(metaJson, heights, data) {
      // CRITERE DE RECETTE de l'ecran de territoire (cf. TerritoryStage.js) :
      // ce compteur doit rester a 1 sur une partie entiere. S'il monte, la
      // scene se reconstruit quelque part — donc l'ecran noir qu'on cherchait a
      // supprimer est revenu, et il vaut mieux le lire dans la console que le
      // decouvrir en salle.
      if (process.env.NODE_ENV !== "production") {
        window.__terrainSceneBuilds = (window.__terrainSceneBuilds || 0) + 1;
        console.info(
          `[TerrainViewer] construction #${window.__terrainSceneBuilds}`,
          `${territoryId} [${(availableLayers || []).join(", ") || "aucune couche"}]`
        );
      }
      const {
        riversData, citiesData, withdrawalsData,
        geologyCodes, geologyData, communeCodes, communesData,
        climateData, dischargeData,
      } = data;
      const communeRecords = communesData?.communes || [];
      const geologyRecords = geologyData?.entities || [];
      const climatePointList = climateData?.points || [];
      const dischargeList = dischargeData?.stations || [];
      const { width: cols, height: rows, cellsize, origin_x: originX, origin_y: originY, zmin, zmax, nodata } = metaJson;

      // --- Catalogue et reglages effectifs, AVANT toute construction -------
      //
      // L'ordre compte : chaque couche doit NAITRE avec le reglage voulu par
      // l'auteur. La construire avec un defaut puis la corriger au premier
      // rendu produirait un clignotement — anodin sur un ecran de bureau,
      // tres visible quand la carte est projetee devant une salle.
      const climateAxesValue = parseClimateAxes(climateData);
      const dischargeAxesValue = parseDischargeAxes(dischargeData);
      const riverLengths = (riversData || []).map((r) => r.length_km || 0);
      const distinctUsages = Array.from(
        new Set((withdrawalsData || []).map((o) => o.usage))
      ).sort();

      const catalogValue = buildCatalog({
        communesData, geologyData,
        climateAxes: climateAxesValue,
        dischargeAxes: dischargeAxesValue,
        cities: citiesData,
        riverLengths,
        usages: distinctUsages,
      });
      const initialSettings = mergeLayerSettings(
        defaultLayerSettings(catalogValue),
        authoredSettingsRef.current
      );
      setCatalog(catalogValue);
      if (onCatalogChangeRef.current) onCatalogChangeRef.current(catalogValue);
      const mount = mountRef.current;

      const zRange = zmax - zmin || 1;
      const halfW = ((cols - 1) * cellsize) / 2;
      const halfH = ((rows - 1) * cellsize) / 2;
      const worldW = cols * cellsize;
      const worldH = rows * cellsize;
      const socleDepthRaw = zRange * SOCLE_DEPTH_RATIO;
      const initialExaggeration = FIXED_EXAGGERATION;
      const initialActiveLayers = new Set(activeLayersRef.current);

      const worldDiag = Math.sqrt(worldW * worldW + worldH * worldH);

      const scene = new THREE.Scene();
      const skyColor = new THREE.Color("#8fb7c9");
      const fogObj = new THREE.FogExp2(skyColor.getHex(), 0.9 / Math.max(worldW, worldH));
      scene.fog = fogEnabledRef.current ? fogObj : null;

      const farPlane = Math.max(200000, worldDiag * 3);
      const camera = new THREE.PerspectiveCamera(45, mount.clientWidth / mount.clientHeight, 10, farPlane);

      const initialPose = cameraPoseRef.current;
      const camAzimuth = initialPose ? initialPose.azimuth : DEFAULT_CAMERA_AZIMUTH;
      const camElevation = initialPose ? initialPose.elevation : DEFAULT_CAMERA_ELEVATION;
      const camDist = initialPose ? initialPose.distance : worldDiag * DEFAULT_CAMERA_DISTANCE_RATIO;
      const camTarget = initialPose?.target || { x: 0, y: 0, z: 0 };
      camera.position.set(
        camTarget.x + camDist * Math.cos(camElevation) * Math.sin(camAzimuth),
        camTarget.y + camDist * Math.sin(camElevation),
        camTarget.z + camDist * Math.cos(camElevation) * Math.cos(camAzimuth)
      );

      const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, MAX_PIXEL_RATIO));
      renderer.setSize(mount.clientWidth, mount.clientHeight);
      renderer.shadowMap.enabled = true;
      renderer.shadowMap.type = THREE.PCFSoftShadowMap;
      // OMBRE CALCULÉE À LA DEMANDE. Le soleil est fixe et le relief ne bouge
      // pas : laisser three.js reprojeter les 750 000 triangles du terrain
      // dans une carte d'ombre 2048² à chaque image revient à refaire soixante
      // fois par seconde une texture rigoureusement identique. On la recalcule
      // donc quand quelque chose qui projette une ombre a effectivement bougé
      // — c'est le rôle d'`invalidateShadows` ci-dessous.
      renderer.shadowMap.autoUpdate = false;
      // RÈGLE À TENIR : tout objet qui porte `castShadow` et dont la position,
      // l'échelle ou la visibilité change en cours de séance doit appeler
      // ceci. Aujourd'hui : les cylindres de prélèvement, les pastilles
      // climat et débit (survol compris). Une ombre figée sur un objet déplacé
      // est un bug silencieux — il ne se voit qu'au vidéoprojecteur.
      const invalidateShadows = () => { renderer.shadowMap.needsUpdate = true; };
      invalidateShadows();
      renderer.outputColorSpace = THREE.SRGBColorSpace;
      mount.appendChild(renderer.domElement);

      const controls = new OrbitControls(camera, renderer.domElement);
      controls.target.set(camTarget.x, camTarget.y, camTarget.z);
      controls.enableDamping = true;
      controls.dampingFactor = 0.08;
      controls.minDistance = 2000;
      controls.maxDistance = worldDiag * 1.4;
      controls.maxPolarAngle = Math.PI * 0.49;
      controls.autoRotate = autoRotateOnRef.current;
      controls.autoRotateSpeed = 0.35;
      controls.update();

      // Etat courant du recentrage optique : pose d'emblee a sa valeur cible
      // (pas d'animation au premier rendu), puis rattrape la cible en douceur
      // depuis animate() quand le panneau change de mode.
      const viewShift = insetsToViewShift(contentInsetsRef.current, mount.clientWidth, mount.clientHeight);
      applyCameraViewShift(camera, mount.clientWidth, mount.clientHeight, viewShift.dx, viewShift.dy);

      // --- masque de validite par sommet / par "quad" (case entre 4 sommets) ---
      const isValid = (r, c) => heights[r * cols + c] !== nodata;
      const quadValid = (r, c) =>
        isValid(r, c) && isValid(r, c + 1) && isValid(r + 1, c) && isValid(r + 1, c + 1);

      const quadValidMask = new Uint8Array((rows - 1) * (cols - 1));
      for (let r = 0; r < rows - 1; r++) {
        for (let c = 0; c < cols - 1; c++) {
          quadValidMask[r * (cols - 1) + c] = quadValid(r, c) ? 1 : 0;
        }
      }

      // --- terrain : sommets + couleurs + index (uniquement les quads valides) ---
      const positions = new Float32Array(cols * rows * 3);
      const colors = new Float32Array(cols * rows * 3);

      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const idx = r * cols + c;
          const h = heights[idx];
          const t = (h - zmin) / zRange;
          positions[idx * 3] = c * cellsize - halfW;
          positions[idx * 3 + 1] = (h - zmin) * initialExaggeration;
          positions[idx * 3 + 2] = r * cellsize - halfH;
          const [cr, cg, cb] = rampColor(t, ELEVATION_RAMP);
          colors[idx * 3] = cr;
          colors[idx * 3 + 1] = cg;
          colors[idx * 3 + 2] = cb;
        }
      }

      const terrainIndices = [];
      for (let r = 0; r < rows - 1; r++) {
        for (let c = 0; c < cols - 1; c++) {
          if (!quadValidMask[r * (cols - 1) + c]) continue;
          const a = r * cols + c;
          const b = a + 1;
          const d = a + cols;
          const e = d + 1;
          terrainIndices.push(a, d, b, b, d, e);
        }
      }

      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
      geometry.setAttribute("color", new THREE.BufferAttribute(colors, 3));
      geometry.setIndex(terrainIndices);
      geometry.computeVertexNormals();

      const material = new THREE.MeshStandardMaterial({
        vertexColors: true,
        roughness: 0.92,
        metalness: 0.02,
      });
      const terrain = new THREE.Mesh(geometry, material);
      terrain.receiveShadow = true;
      terrain.castShadow = true;
      scene.add(terrain);

      // --- geologie (BD LISA, BRGM) : raster precalcule cote Python
      // (build_overlays.py::build_geology), simple lookup ici.
      //
      // Le raster porte un index d'ENTITE et non une classe de couleur : les
      // quatre classements de BD LISA (theme, nature, milieu, etat) sont donc
      // quatre colorations d'un meme fichier — de quelle formation s'agit-il,
      // y a-t-il de l'eau, circule-t-elle vite, est-elle protegee. ---
      let geologyMesh = null;
      let geologyGeometry = null;
      let geologyBaseColors = null;
      if (geologyCodes && geologyCodes.length === cols * rows && geologyRecords.length) {
        const { colors } = resolveIndicator(
          geologyData.indicators, geologyRecords, initialSettings.geology?.indicator
        );
        geologyBaseColors = colors;

        const geologyColors = new Float32Array(cols * rows * 3);
        paintRasterColors(geologyColors, geologyCodes, geologyBaseColors);
        const geologyPositions = positions.slice();
        for (let i = 1; i < geologyPositions.length; i += 3) geologyPositions[i] += GEOLOGY_Y_OFFSET;
        geologyGeometry = new THREE.BufferGeometry();
        geologyGeometry.setAttribute("position", new THREE.BufferAttribute(geologyPositions, 3));
        geologyGeometry.setAttribute("color", new THREE.BufferAttribute(geologyColors, 3));
        geologyGeometry.setIndex(terrainIndices);
        const geologyMaterial = new THREE.MeshBasicMaterial({
          vertexColors: true,
          transparent: true,
          opacity: initialSettings.geology?.opacity ?? 0.8,
          depthWrite: false,
          polygonOffset: true,
          polygonOffsetFactor: -5,
          polygonOffsetUnits: -5,
        });
        geologyMesh = new THREE.Mesh(geologyGeometry, geologyMaterial);
        geologyMesh.visible = initialActiveLayers.has("geology");
        scene.add(geologyMesh);

        setGeologyIndicators(geologyData.indicators || []);
      }

      // --- communes : nappe drapee coloree par l'indicateur choisi + trace
      // des limites. Comme la geologie, la couleur vient d'un raster
      // precalcule aligne sur la grille du terrain — mais ici le raster ne
      // porte que l'IDENTITE de la commune : c'est le front qui choisit ce
      // qu'il en montre (population, specialisation, arretes secheresse...),
      // sans rien retelecharger. ---
      let communeMesh = null;
      let communeGeometry = null;
      let communeBorderMesh = null;
      let communeHighlightMesh = null;
      let communeBorders = null;
      let communeCells = null;
      let communeBaseColors = null;
      if (communeCodes && communeCodes.length === cols * rows && communeRecords.length) {
        const { colors } = resolveIndicator(
          communesData.indicators, communeRecords, initialSettings.communes?.indicator
        );
        communeBaseColors = colors;

        const communeColorArray = new Float32Array(cols * rows * 3);
        paintRasterColors(communeColorArray, communeCodes, communeBaseColors);
        const communePositions = positions.slice();
        for (let i = 1; i < communePositions.length; i += 3) communePositions[i] += COMMUNE_CHOROPLETH_Y_OFFSET;
        communeGeometry = new THREE.BufferGeometry();
        communeGeometry.setAttribute("position", new THREE.BufferAttribute(communePositions, 3));
        communeGeometry.setAttribute("color", new THREE.BufferAttribute(communeColorArray, 3));
        communeGeometry.setIndex(terrainIndices);
        const communeMaterial = new THREE.MeshBasicMaterial({
          vertexColors: true,
          transparent: true,
          opacity: initialSettings.communes?.opacity ?? 0.55,
          depthWrite: false,
          polygonOffset: true,
          polygonOffsetFactor: -6,
          polygonOffsetUnits: -6,
        });
        communeMesh = new THREE.Mesh(communeGeometry, communeMaterial);
        communeMesh.visible = initialActiveLayers.has("communes");
        scene.add(communeMesh);

        communeCells = buildCommuneCellIndex(communeCodes);

        // Limites : une seule geometrie, dont les segments sont ranges par
        // commune. Le mesh de surlignage partage ses attributs (donc le meme
        // buffer GPU) et n'en dessine qu'une tranche via setDrawRange.
        communeBorders = buildCommuneBorders({ codes: communeCodes, heights, cols, rows, cellsize, zmin, nodata });
        const borderGeometry = new THREE.BufferGeometry();
        borderGeometry.setAttribute("position", new THREE.BufferAttribute(communeBorders.positions, 3));
        applyDrapedExaggeration(borderGeometry, communeBorders.rawH, initialExaggeration, COMMUNE_BORDER_Y_OFFSET);
        communeBorderMesh = new THREE.LineSegments(
          borderGeometry,
          new THREE.LineBasicMaterial({ color: "#1d3038", transparent: true, opacity: 0.42, depthWrite: false })
        );
        communeBorderMesh.visible = initialActiveLayers.has("communes") && initialSettings.communes?.borders !== false;
        scene.add(communeBorderMesh);

        const highlightGeometry = new THREE.BufferGeometry();
        highlightGeometry.setAttribute("position", borderGeometry.attributes.position);
        highlightGeometry.setDrawRange(0, 0);
        communeHighlightMesh = new THREE.LineSegments(
          highlightGeometry,
          new THREE.LineBasicMaterial({ color: "#ffffff", transparent: true, opacity: 0.95, depthWrite: false })
        );
        scene.add(communeHighlightMesh);

        setCommunes(communeRecords);
        setCommuneIndicators(communesData.indicators || []);
      }

      // --- socle : murs le long du contour decoupe + dalle de base ---
      const wallEdges = [];
      const addEdge = (r0, c0, r1, c1) => {
        const i0 = r0 * cols + c0;
        const i1 = r1 * cols + c1;
        wallEdges.push({
          ax: c0 * cellsize - halfW,
          az: r0 * cellsize - halfH,
          rawHa: heights[i0] - zmin,
          bx: c1 * cellsize - halfW,
          bz: r1 * cellsize - halfH,
          rawHb: heights[i1] - zmin,
        });
      };
      const nQCols = cols - 1;
      for (let r = 0; r < rows - 1; r++) {
        for (let c = 0; c < nQCols; c++) {
          if (!quadValidMask[r * nQCols + c]) continue;
          const upValid = r > 0 && quadValidMask[(r - 1) * nQCols + c];
          const downValid = r < rows - 2 && quadValidMask[(r + 1) * nQCols + c];
          const leftValid = c > 0 && quadValidMask[r * nQCols + c - 1];
          const rightValid = c < nQCols - 1 && quadValidMask[r * nQCols + c + 1];
          if (!upValid) addEdge(r, c, r, c + 1);
          if (!downValid) addEdge(r + 1, c + 1, r + 1, c);
          if (!leftValid) addEdge(r + 1, c, r, c);
          if (!rightValid) addEdge(r, c + 1, r + 1, c + 1);
        }
      }

      let wallMesh = null;
      if (wallEdges.length) {
        const wallColors = new Float32Array(wallEdges.length * 18);
        const top = lerpColor(WALL_TOP_COLOR, WALL_BOTTOM_COLOR, 0);
        const bot = lerpColor(WALL_TOP_COLOR, WALL_BOTTOM_COLOR, 1);
        for (let i = 0; i < wallEdges.length; i++) {
          const o = i * 18;
          const set = (off, col) => {
            wallColors[off] = col[0];
            wallColors[off + 1] = col[1];
            wallColors[off + 2] = col[2];
          };
          set(o, top); set(o + 3, top); set(o + 6, bot);
          set(o + 9, top); set(o + 12, bot); set(o + 15, bot);
        }
        const wallGeo = new THREE.BufferGeometry();
        wallGeo.setAttribute(
          "position",
          new THREE.BufferAttribute(buildWallPositions(wallEdges, initialExaggeration, socleDepthRaw), 3)
        );
        wallGeo.setAttribute("color", new THREE.BufferAttribute(wallColors, 3));
        wallGeo.computeVertexNormals();
        const wallMat = new THREE.MeshStandardMaterial({
          vertexColors: true,
          roughness: 1,
          metalness: 0,
          side: THREE.DoubleSide,
        });
        wallMesh = new THREE.Mesh(wallGeo, wallMat);
        wallMesh.receiveShadow = true;
        scene.add(wallMesh);
      }

      // --- lumieres ---
      const ambient = new THREE.HemisphereLight("#cfe8f0", "#4a3d2c", 0.9);
      scene.add(ambient);

      const sun = new THREE.DirectionalLight("#fff3d6", 1.35);
      sun.position.set(worldW * 0.4, Math.max(worldW, worldH) * 0.6, -worldH * 0.25);
      sun.castShadow = true;
      sun.shadow.mapSize.set(2048, 2048);
      sun.shadow.camera.left = -halfW * 1.1;
      sun.shadow.camera.right = halfW * 1.1;
      sun.shadow.camera.top = halfH * 1.1;
      sun.shadow.camera.bottom = -halfH * 1.1;
      sun.shadow.camera.near = 10;
      sun.shadow.camera.far = Math.max(worldW, worldH) * 2;
      sun.shadow.bias = -0.0015;
      scene.add(sun);

      // --- echantillonnage terrain (utilise par rivieres + villes) ---
      const northY = originY + rows * cellsize;
      const sampleAt = (px, py) => {
        const fc = (px - originX) / cellsize;
        const fr = (northY - py) / cellsize;
        const c0 = Math.floor(fc);
        const r0 = Math.floor(fr);
        if (c0 < 0 || r0 < 0 || c0 >= cols - 1 || r0 >= rows - 1) return null;
        const h00 = heights[r0 * cols + c0];
        const h10 = heights[r0 * cols + c0 + 1];
        const h01 = heights[(r0 + 1) * cols + c0];
        const h11 = heights[(r0 + 1) * cols + c0 + 1];
        if (h00 === nodata || h10 === nodata || h01 === nodata || h11 === nodata) return null;
        const fx = fc - c0;
        const fy = fr - r0;
        const h = h00 * (1 - fx) * (1 - fy) + h10 * fx * (1 - fy) + h01 * (1 - fx) * fy + h11 * fx * fy;
        return { x: fc * cellsize - halfW, z: fr * cellsize - halfH, rawH: h - zmin };
      };

      // Meme interpolation, mais indexee en coordonnees de SCENE : les bords
      // d'un ruban de riviere sont calcules la, pas dans le repere de la
      // donnee source. Rend null hors grille ou sur un trou, a charge de
      // l'appelant de choisir un repli.
      const heightAtWorld = (x, z) => {
        const fc = (x + halfW) / cellsize;
        const fr = (z + halfH) / cellsize;
        const c0 = Math.floor(fc);
        const r0 = Math.floor(fr);
        if (c0 < 0 || r0 < 0 || c0 >= cols - 1 || r0 >= rows - 1) return null;
        const h00 = heights[r0 * cols + c0];
        const h10 = heights[r0 * cols + c0 + 1];
        const h01 = heights[(r0 + 1) * cols + c0];
        const h11 = heights[(r0 + 1) * cols + c0 + 1];
        if (h00 === nodata || h10 === nodata || h01 === nodata || h11 === nodata) return null;
        const fx = fc - c0;
        const fy = fr - r0;
        return (
          h00 * (1 - fx) * (1 - fy) + h10 * fx * (1 - fy) +
          h01 * (1 - fx) * fy + h11 * fx * fy
        ) - zmin;
      };

      // --- rivieres : un ruban drape par polyligne valide, coupe aux trous ---
      const riversGroup = new THREE.Group();
      const riverRibbons = [];
      const riverMaxHalfWidth = Math.max(worldDiag * 0.0045, 350);
      const riverMinHalfWidth = Math.max(worldDiag * 0.00015, 12);
      const riverLengthDatasetMax = catalogValue.rivers.lengthMax;
      const initialLengthCap = initialSettings.rivers?.length_cap_km || riverLengthDatasetMax;
      const riverHalfWidthFor = (lengthKm, lengthCapKm) =>
        Math.max(riverMinHalfWidth, (riverMaxHalfWidth / lengthCapKm) * Math.min(lengthKm || 0, lengthCapKm));
      const riverBandLength = Math.max(worldDiag * 0.01, 300);
      const riverTexture = createFlowTexture();
      const riverMaterial = new THREE.MeshBasicMaterial({
        color: new THREE.Color(RIVER_COLOR),
        map: riverTexture,
        transparent: true,
        opacity: 0.9,
        side: THREE.DoubleSide,
        depthWrite: false,
        // Meme precaution que pour la geologie et les communes. Le ruban est
        // pose 3 m au-dessus du relief, mais son altitude vient d'une
        // interpolation bilineaire de la grille alors que le terrain est
        // dessine en triangles : sur un versant raide, l'ecart entre les deux
        // depasse la garde et le ruban disparait par plaques. Ce decalage ne
        // joue que sur le test de profondeur contre le terrain — les autres
        // nappes n'ecrivent pas de profondeur, leur ordre ne bouge pas.
        polygonOffset: true,
        polygonOffsetFactor: -7,
        polygonOffsetUnits: -7,
      });
      // Ce que `applyRiverWidth` a besoin de savoir pour reposer les bords sur
      // le relief a chaque changement de largeur.
      const riverDrape = { heightAtWorld, exaggeration: initialExaggeration };
      // REBATIR, ET PAS SEULEMENT REDIMENSIONNER. Le pas de reechantillonnage
      // se deduit de la largeur dessinee : deplacer les sommets d'une
      // geometrie taillee pour une autre largeur ramenerait le froissement des
      // que l'animateur pousse le curseur d'epaisseur. `riverBuild.key` evite
      // de refaire 700 rubans pour des reglages qu'on a deja batis.
      const riverBuild = { key: "" };
      const rebuildRivers = (lengthCapKm, widthScale) => {
        const cap = lengthCapKm || riverLengthDatasetMax;
        const scale = widthScale ?? 1;
        const key = `${cap}|${scale}`;
        if (riverBuild.key === key) return;
        riverBuild.key = key;

        riverRibbons.forEach((ribbon) => ribbon.geometry.dispose());
        riverRibbons.length = 0;
        riversGroup.clear();

        (riversData || []).forEach((river) => {
          const halfWidth = riverHalfWidthFor(river.length_km, cap) * scale;
          // LE PAS SUIT LA LARGEUR. C'est tout l'objet du correctif : la
          // Loire, dessinee large de 1 600 m, est reechantillonnee tous les
          // ~730 m et cesse de se replier dans ses meandres ; un ruisseau
          // large de 54 m garde un point tous les 25 m et ne perd rien de son
          // trace.
          const step = Math.max(RIVER_MIN_RESAMPLE_STEP, halfWidth * RIVER_RESAMPLE_RATIO);
          const rows = ribbonRows(halfWidth, cellsize);
          (river.polylines || []).forEach((sourceLine) => {
            const line = smoothLine(resampleLine(sourceLine, step), RIVER_SMOOTHING_PASSES);
            let run = [];
            const flush = () => {
              if (run.length >= 2) {
                const normals = computeRiverNormals(run);
                const ribbon = buildRiverRibbon(run, normals, halfWidth, riverBandLength, riverDrape, rows);
                riversGroup.add(new THREE.Mesh(ribbon.geometry, riverMaterial));
                ribbon.currentHalfWidth = halfWidth;
                ribbon.river = { name: river.name, length_km: river.length_km };
                riverRibbons.push(ribbon);
              }
              run = [];
            };
            line.forEach(([x, y]) => {
              const s = sampleAt(x, y);
              if (s) run.push(s);
              else flush();
            });
            flush();
          });
        });
      };
      rebuildRivers(initialLengthCap, initialSettings.rivers?.width_scale ?? 1);
      riversGroup.visible = initialActiveLayers.has("rivers");
      scene.add(riversGroup);

      // --- villes : halo + faisceau + etiquette, interactifs (survol/clic) ---
      //
      // Le datapack en livre beaucoup (60), la vue en montre peu : la taille
      // du marqueur depend du rang DANS L'ENSEMBLE RETENU, donc les marqueurs
      // sont reconstruits quand la selection change plutot que simplement
      // masques. `rebuildCities` est range dans stateRef pour que l'effet de
      // filtrage puisse le rappeler sans reconstruire toute la scene.
      const citiesGroup = new THREE.Group();
      const cityState = { markers: [], hitMeshes: [], builtViewportHeight: 0 };
      // Les etiquettes sont dessinees a la definition de l'ecran (cf.
      // citiesLayer.createLabelSprite) : la hauteur de la vue fait partie de
      // ce qu'il faut savoir pour les construire.
      const labelScreen = () => ({
        fov: camera.fov,
        viewportHeight: mount.clientHeight || 1,
        pixelRatio: renderer.getPixelRatio(),
      });
      const rebuildCities = (citySettings) => {
        cityState.markers.forEach((marker) => {
          citiesGroup.remove(marker.group);
          disposeCityMarker(marker);
        });
        const screen = labelScreen();
        cityState.builtViewportHeight = screen.viewportHeight;
        const selected = selectCities(citiesData, citySettings);
        const markers = [];
        selected.forEach((city) => {
          const marker = createCityMarker(city, {
            sampleAt, worldDiag, zRange, exaggeration: initialExaggeration, screen,
          });
          if (!marker) return;
          marker.hitMesh.userData.markerIndex = markers.length;
          citiesGroup.add(marker.group);
          markers.push(marker);
        });
        cityState.markers = markers;
        cityState.hitMeshes = markers.map((m) => m.hitMesh);
      };
      rebuildCities(initialSettings.cities);
      setCityList(citiesData || []);
      // Selection deja en place : memorisee pour que l'effet de filtrage ne
      // refabrique pas immediatement les memes etiquettes (chacune porte un
      // canvas de texte, ce n'est pas gratuit).
      const initialCityKey = selectCities(citiesData, initialSettings.cities)
        .map((c) => `${c.code_insee}:${c.tier}`).join(",");
      citiesGroup.visible = initialActiveLayers.has("cities");
      scene.add(citiesGroup);

      // --- zones d'interet (points_of_interest) : marqueurs definis par
      // l'auteur, propres a l'etape courante (pas au territoire) — se
      // reconstruisent via un effet dedie (pointsKey) quand `points` change
      // d'une etape a l'autre, sans reconstruire toute la scene. Construits
      // ici une premiere fois avec la valeur courante (pointsRef).
      const poiGroup = new THREE.Group();
      const initialPoiMarkers = (pointsRef.current || []).map((point, index, all) => createPoiMarker(point, all.length > 1 ? index : null, worldDiag));
      initialPoiMarkers.forEach((m, i) => {
        m.hitMesh.userData.markerIndex = i;
        poiGroup.add(m.group);
      });
      scene.add(poiGroup);

      // --- prelevements en eau (Hub'Eau) : InstancedMesh ---
      let withdrawalMesh = null;
      let withdrawalHitMesh = null;
      const withdrawalMeta = [];
      const withdrawalDummy = new THREE.Object3D();
      if (withdrawalsData && withdrawalsData.length) {
        const validPoints = [];
        withdrawalsData.forEach((o) => {
          const sample = sampleAt(o.x, o.y);
          if (sample) validPoints.push({ o, sample });
        });

        const usageColorMap = new Map(
          distinctUsages.map((u, i) => [u, new THREE.Color(USAGE_PALETTE[i % USAGE_PALETTE.length])])
        );

        const barGeo = new THREE.CylinderGeometry(1, 1, 1, 8);
        const withdrawalMat = new THREE.MeshStandardMaterial({ roughness: 0.35, metalness: 0 });
        withdrawalMesh = new THREE.InstancedMesh(barGeo, withdrawalMat, validPoints.length);
        withdrawalMesh.castShadow = true;

        withdrawalHitMesh = new THREE.InstancedMesh(
          barGeo,
          new THREE.MeshBasicMaterial({ visible: false }),
          validPoints.length
        );

        validPoints.forEach(({ o, sample }, i) => {
          const wmeta = {
            index: i,
            data: o,
            rawH: sample.rawH,
            x: sample.x,
            z: sample.z,
            baseRadius: 0,
            hitRadius: 0,
            baseHeight: 0,
            baseColor: usageColorMap.get(o.usage) || new THREE.Color("#ffffff"),
            hiddenByFilter: false,
            currentY: 0,
          };
          withdrawalMesh.setColorAt(i, wmeta.baseColor);
          withdrawalMeta.push(wmeta);
        });
        const initialHiddenUsages = new Set(initialSettings.withdrawals?.usages || []);
        recomputeWithdrawalScales(
          withdrawalMeta, initialHiddenUsages, worldDiag, zRange,
          initialSettings.withdrawals?.bar_width ?? 1,
          initialSettings.withdrawals?.bar_height ?? 1.35
        );
        withdrawalMeta.forEach((wmeta) => {
          wmeta.hiddenByFilter = initialHiddenUsages.has(wmeta.data.usage);
        });
        withdrawalMeta.forEach((wmeta) => {
          applyWithdrawalTransform(withdrawalMesh, withdrawalDummy, wmeta, initialExaggeration, wmeta.baseRadius);
          applyWithdrawalTransform(withdrawalHitMesh, withdrawalDummy, wmeta, initialExaggeration, wmeta.hitRadius);
        });
        withdrawalMesh.instanceMatrix.needsUpdate = true;
        if (withdrawalMesh.instanceColor) withdrawalMesh.instanceColor.needsUpdate = true;
        withdrawalHitMesh.instanceMatrix.needsUpdate = true;
        withdrawalMesh.visible = initialActiveLayers.has("withdrawals");
        scene.add(withdrawalMesh);
        scene.add(withdrawalHitMesh);

        setUsageList(
          distinctUsages.map((u) => ({
            usage: u,
            color: `#${usageColorMap.get(u).getHexString()}`,
            count: validPoints.reduce((n, p) => n + (p.o.usage === u ? 1 : 0), 0),
          }))
        );
      }

      // --- climat : nappe interpolee depuis des points de mesure + les
      // points eux-memes en marqueurs. La nappe n'est pas precalculee cote
      // Python (273 combinaisons variable/horizon/mois, cf. climateLayer.js) :
      // on interpole ici, via une grille intermediaire dont les poids sont
      // calcules une seule fois -> changer de mois ne coute que quelques ms.
      let climateMesh = null;
      let climateGeometry = null;
      let climateInterpolator = null;
      let climateStations = null;
      if (climatePointList.length) {
        const axes = climateAxesValue;
        if (axes.variables.length && axes.horizons.length) {
          const variable = axes.variables.find((v) => v.id === initialSettings.climate?.variable)
            || axes.variables[0];
          const horizon = axes.horizons.includes(initialSettings.climate?.horizon)
            ? initialSettings.climate.horizon
            : axes.horizons[0];
          const period = axes.periods.includes(initialSettings.climate?.period)
            ? initialSettings.climate.period
            : (axes.periods.includes("annuel") ? "annuel" : axes.periods[0]);
          const domain = climateDomain(climatePointList, variable, period, axes.horizons);

          climateInterpolator = createClimateInterpolator({
            points: climatePointList, cols, rows, cellsize, originX, originY,
            smoothingM: (initialSettings.climate?.smoothing_km ?? 6) * 1000,
          });

          const climateColorArray = new Float32Array(cols * rows * 3);
          climateInterpolator.fillColors(
            climateColorArray, climateValues(climatePointList, variable, horizon, period), domain, variable.ramp
          );
          const climatePositions = positions.slice();
          for (let i = 1; i < climatePositions.length; i += 3) climatePositions[i] += CLIMATE_Y_OFFSET;
          climateGeometry = new THREE.BufferGeometry();
          climateGeometry.setAttribute("position", new THREE.BufferAttribute(climatePositions, 3));
          climateGeometry.setAttribute("color", new THREE.BufferAttribute(climateColorArray, 3));
          climateGeometry.setIndex(terrainIndices);
          climateMesh = new THREE.Mesh(
            climateGeometry,
            new THREE.MeshBasicMaterial({
              vertexColors: true,
              transparent: true,
              opacity: initialSettings.climate?.opacity ?? 0.55,
              depthWrite: false,
              polygonOffset: true,
              polygonOffsetFactor: -7,
              polygonOffsetUnits: -7,
            })
          );
          climateMesh.visible = initialActiveLayers.has("climate");
          scene.add(climateMesh);

          climateStations = createClimateStations({
            points: climatePointList, sampleAt, worldDiag, zRange, exaggeration: initialExaggeration,
          });
          if (climateStations) {
            paintClimateStations(climateStations, variable, horizon, period, domain);
            const stationsOn = initialActiveLayers.has("climate")
              && initialSettings.climate?.stations !== false;
            climateStations.mesh.visible = stationsOn;
            climateStations.hitMesh.visible = stationsOn;
            scene.add(climateStations.mesh);
            scene.add(climateStations.hitMesh);
          }

          setClimatePoints(climatePointList);
          setClimateAxes(axes);
        }
      }

      // --- debit : marqueurs de stations hydrometriques. Pas de nappe : un
      // debit est la propriete d'un cours d'eau en un point, interpoler entre
      // deux bassins versants ne voudrait rien dire (cf. dischargeLayer.js).
      // C'est la seule couche qui aille jusqu'a 2100.
      let dischargeMarkers = null;
      if (dischargeList.length) {
        const axes = dischargeAxesValue;
        if (axes.metrics.length && axes.horizons.length) {
          const metric = axes.metrics.find((m) => m.id === initialSettings.discharge?.metric)
            || axes.metrics[0];
          const horizon = axes.horizons.includes(initialSettings.discharge?.horizon)
            ? initialSettings.discharge.horizon
            : axes.horizons[0];
          dischargeMarkers = createDischargeStations({
            stations: dischargeList, sampleAt, worldDiag, zRange, exaggeration: initialExaggeration,
          });
          if (dischargeMarkers) {
            paintDischargeStations(dischargeMarkers, metric, horizon);
            dischargeMarkers.mesh.visible = initialActiveLayers.has("discharge");
            dischargeMarkers.hitMesh.visible = initialActiveLayers.has("discharge");
            scene.add(dischargeMarkers.mesh);
            scene.add(dischargeMarkers.hitMesh);
          }
          setDischargeStations(dischargeList);
          setDischargeAxes(axes);
        }
      }

      // Plan horizontal servant uniquement au survol des cours d'eau : le rayon
      // du curseur le coupe pour obtenir un (x, z), le segment de riviere le
      // plus proche est ensuite cherche en 2D. Sa hauteur n'a donc pas besoin
      // d'etre exacte, seulement proche des fonds de vallee ou courent les
      // rivieres, sinon le point vise derive sous un angle de camera rasant.
      const riverPickPlane = new THREE.Plane(
        new THREE.Vector3(0, 1, 0),
        -(zRange * RIVER_PICK_HEIGHT_RATIO * initialExaggeration)
      );

      // Point du relief vise par le curseur : sert a savoir sur quelle
      // commune on pointe (cf. createTerrainPicker pour le pourquoi de cette
      // methode plutot qu'un raycast contre le maillage terrain).
      const pickTerrainCell = createTerrainPicker({ heights, cols, rows, cellsize, zmin, zmax, nodata });

      // A appeler apres `pick()`, qui a deja oriente `raycaster` sur le
      // curseur. Un seul sondage du relief sert les deux couches rasterisees :
      // elles partagent la grille du terrain, donc l'index de cellule.
      const cellAt = () => {
        if (!communeMesh?.visible && !geologyMesh?.visible) return null;
        return pickTerrainCell(raycaster.ray, initialExaggeration);
      };
      const communeAtCell = (cell) => {
        if (!cell || !communeMesh || !communeMesh.visible) return -1;
        const id = communeCodes[cell.index];
        return id === COMMUNE_NODATA ? -1 : id;
      };
      const geologyAtCell = (cell) => {
        if (!cell || !geologyMesh || !geologyMesh.visible) return null;
        return geologyEntityAt(geologyCodes, geologyRecords, cell.index);
      };
      const communeAt = () => communeAtCell(cellAt());

      // Surlignage : la commune survolee est eclaircie (ses cellules) et son
      // contour ressort (tranche dediee de la geometrie des limites).
      const applyCommuneHover = (index) => {
        const s = stateRef.current;
        const previous = s.hoveredCommuneIndex ?? -1;
        if (index === previous) return;
        const colorAttribute = communeGeometry.attributes.color;
        const baseColors = s.communeBaseColors || communeBaseColors;
        if (previous >= 0) paintCells(colorAttribute.array, communeCells.get(previous), baseColors[previous]);
        if (index >= 0) paintCells(colorAttribute.array, communeCells.get(index), communeHoverColor(baseColors[index]));
        colorAttribute.needsUpdate = true;
        const range = index >= 0 ? communeBorders.ranges.get(index) : null;
        communeHighlightMesh.geometry.setDrawRange(range ? range.start : 0, range ? range.count : 0);
        s.hoveredCommuneIndex = index;
      };

      // --- survol / clic : villes + prelevements ---
      const raycaster = new THREE.Raycaster();
      const pointerNdc = new THREE.Vector2();
      let hoveredMarkerIndex = -1;
      let hoveredWithdrawalIndex = -1;

      const pick = (event) => {
        const rect = renderer.domElement.getBoundingClientRect();
        pointerNdc.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        pointerNdc.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
        raycaster.setFromCamera(pointerNdc, camera);

        // Meme garde que pour les prelevements : une couche eteinte ne doit
        // pas rester cliquable (three.js raycaste les objets invisibles).
        const cityHits = citiesGroup.visible ? raycaster.intersectObjects(cityState.hitMeshes, false) : [];
        const withdrawalHits =
          withdrawalHitMesh && withdrawalMesh.visible ? raycaster.intersectObject(withdrawalHitMesh, false) : [];
        // poiHitMeshes lu depuis stateRef (pas une closure figee) : contrairement
        // aux villes/prelevements, les marqueurs de zones d'interet sont
        // reconstruits apres coup (effet pointsKey) quand on change d'etape.
        const poiHitMeshes = stateRef.current.poiHitMeshes || [];
        const poiHits = poiHitMeshes.length ? raycaster.intersectObjects(poiHitMeshes, false) : [];
        const climateHits =
          climateStations && climateStations.mesh.visible
            ? raycaster.intersectObject(climateStations.hitMesh, false)
            : [];
        const dischargeHits =
          dischargeMarkers && dischargeMarkers.mesh.visible
            ? raycaster.intersectObject(dischargeMarkers.hitMesh, false)
            : [];

        // Le marqueur le plus proche de la camera l'emporte, quel que soit
        // son type (une liste triee plutot qu'une cascade de comparaisons
        // deux a deux, qui devenait illisible a partir de trois familles).
        const candidates = [];
        if (cityHits.length) {
          candidates.push({ d: cityHits[0].distance, hit: { type: "city", index: cityHits[0].object.userData.markerIndex } });
        }
        if (withdrawalHits.length) {
          candidates.push({ d: withdrawalHits[0].distance, hit: { type: "withdrawal", index: withdrawalHits[0].instanceId } });
        }
        if (poiHits.length) {
          candidates.push({ d: poiHits[0].distance, hit: { type: "poi", index: poiHits[0].object.userData.markerIndex } });
        }
        if (climateHits.length) {
          candidates.push({ d: climateHits[0].distance, hit: { type: "climate", index: climateHits[0].instanceId } });
        }
        if (dischargeHits.length) {
          candidates.push({ d: dischargeHits[0].distance, hit: { type: "discharge", index: dischargeHits[0].instanceId } });
        }
        if (!candidates.length) return null;
        candidates.sort((a, b) => a.d - b.d);
        return candidates[0].hit;
      };

      // Survol d'une station : la pastille grossit et blanchit, sans que sa
      // couleur de fond (qui code la valeur) soit perdue.
      let hoveredStationIndex = -1;
      const setStationHover = (index, hovered) => {
        if (index < 0 || !climateStations) return;
        const station = climateStations.meta[index];
        applyClimateStationTransform(
          climateStations.mesh, climateStations.dummy, station,
          initialExaggeration, hovered ? station.radius * 1.9 : station.radius
        );
        climateStations.mesh.setColorAt(
          index,
          hovered ? station.baseColor.clone().lerp(new THREE.Color("#ffffff"), 0.55) : station.baseColor
        );
        climateStations.mesh.instanceMatrix.needsUpdate = true;
        if (climateStations.mesh.instanceColor) climateStations.mesh.instanceColor.needsUpdate = true;
        invalidateShadows(); // la pastille grossit : son ombre aussi
      };

      // Meme traitement que les stations climatiques.
      let hoveredDischargeIndex = -1;
      const setDischargeHover = (index, hovered) => {
        if (index < 0 || !dischargeMarkers) return;
        const station = dischargeMarkers.meta[index];
        applyDischargeStationTransform(
          dischargeMarkers.mesh, dischargeMarkers.dummy, station,
          initialExaggeration, hovered ? station.radius * 1.8 : station.radius
        );
        dischargeMarkers.mesh.setColorAt(
          index,
          hovered ? station.baseColor.clone().lerp(new THREE.Color("#ffffff"), 0.55) : station.baseColor
        );
        dischargeMarkers.mesh.instanceMatrix.needsUpdate = true;
        if (dischargeMarkers.mesh.instanceColor) dischargeMarkers.mesh.instanceColor.needsUpdate = true;
        invalidateShadows();
      };

      const setWithdrawalHover = (index, hovered) => {
        if (index < 0 || !withdrawalMesh) return;
        const wmeta = withdrawalMeta[index];
        const radius = hovered ? wmeta.baseRadius * 2 : wmeta.baseRadius;
        withdrawalDummy.position.set(wmeta.x, wmeta.currentY + wmeta.baseHeight / 2, wmeta.z);
        withdrawalDummy.scale.set(radius, wmeta.baseHeight, radius);
        withdrawalDummy.updateMatrix();
        withdrawalMesh.setMatrixAt(index, withdrawalDummy.matrix);
        withdrawalMesh.setColorAt(
          index,
          hovered ? wmeta.baseColor.clone().lerp(new THREE.Color("#ffffff"), 0.5) : wmeta.baseColor
        );
        withdrawalMesh.instanceMatrix.needsUpdate = true;
        if (withdrawalMesh.instanceColor) withdrawalMesh.instanceColor.needsUpdate = true;
        invalidateShadows();
      };

      // MANIPULATION EN COURS ? C'est ce que lit la boucle d'animation pour
      // décider de rendre à la fréquence de l'écran ou de se brider (cf.
      // IDLE_FPS). On ne peut pas s'appuyer sur l'événement "change"
      // d'OrbitControls : la rotation automatique le déclenche à chaque image,
      // et la carte ne serait alors jamais considérée au repos.
      let activeUntil = 0;
      const markActive = () => { activeUntil = performance.now() + ACTIVITY_TAIL_MS; };

      let lastGroundHoverCheck = 0;
      const riverPlaneHit = new THREE.Vector3();
      const poiProjectVector = new THREE.Vector3();
      // Second point projete, au sommet de la pastille : la difference avec le
      // centre donne son rayon a l'ecran, donc ou accrocher le trait.
      const poiEdgeVector = new THREE.Vector3();
      const handlePointerMove = (event) => {
        markActive();
        if (placementModeRef.current) {
          renderer.domElement.style.cursor = "crosshair";
          return;
        }
        const hit = pick(event);
        hoveredMarkerIndex = hit?.type === "city" ? hit.index : -1;
        const newWithdrawalIndex = hit?.type === "withdrawal" ? hit.index : -1;
        if (newWithdrawalIndex !== hoveredWithdrawalIndex) {
          setWithdrawalHover(hoveredWithdrawalIndex, false);
          setWithdrawalHover(newWithdrawalIndex, true);
          hoveredWithdrawalIndex = newWithdrawalIndex;
        }
        const newStationIndex = hit?.type === "climate" ? hit.index : -1;
        if (newStationIndex !== hoveredStationIndex) {
          setStationHover(hoveredStationIndex, false);
          setStationHover(newStationIndex, true);
          hoveredStationIndex = newStationIndex;
        }
        const newDischargeIndex = hit?.type === "discharge" ? hit.index : -1;
        if (newDischargeIndex !== hoveredDischargeIndex) {
          setDischargeHover(hoveredDischargeIndex, false);
          setDischargeHover(newDischargeIndex, true);
          hoveredDischargeIndex = newDischargeIndex;
        }
        renderer.domElement.style.cursor = hit ? "pointer" : "default";

        // Survols "au sol" (cours d'eau, communes) : throttle commun a
        // ~20 verifications/s. Les faire a chaque mousemove brut (100+/s)
        // reviendrait a chercher le segment le plus proche parmi des
        // centaines de milliers de points, et a re-sonder le relief, pour
        // rien.
        const now = performance.now();
        if (now - lastGroundHoverCheck < 50) return;
        lastGroundHoverCheck = now;

        if (riversGroup.visible) {
          const gotPlaneHit = raycaster.ray.intersectPlane(riverPickPlane, riverPlaneHit);
          const river = gotPlaneHit ? findHoveredRiver(riverRibbons, riverPlaneHit.x, riverPlaneHit.z, worldDiag) : null;
          setHoveredRiver(river ? { ...river, clientX: event.clientX, clientY: event.clientY } : null);
        } else {
          setHoveredRiver(null);
        }

        // Un marqueur sous le curseur l'emporte : sa propre infobulle suffit,
        // inutile d'y superposer celle de la commune qui est dessous.
        const cell = hit ? null : cellAt();
        const communeIndex = communeAtCell(cell);
        applyCommuneHover(communeIndex);
        setHoveredCommune(
          communeIndex >= 0
            ? { commune: communeRecords[communeIndex], clientX: event.clientX, clientY: event.clientY }
            : null
        );
        // L'infobulle geologique ne s'affiche que si aucune commune ne la
        // precede : les deux nappes se superposent, deux infobulles au meme
        // endroit seraient illisibles.
        const entity = communeIndex >= 0 ? null : geologyAtCell(cell);
        setHoveredGeology(
          entity ? { entity, clientX: event.clientX, clientY: event.clientY } : null
        );
      };
      const handlePointerLeave = () => {
        setHoveredRiver(null);
        applyCommuneHover(-1);
        setHoveredCommune(null);
        setHoveredGeology(null);
        setStationHover(hoveredStationIndex, false);
        hoveredStationIndex = -1;
        setDischargeHover(hoveredDischargeIndex, false);
        hoveredDischargeIndex = -1;
      };
      // Position du dernier appui : sert a distinguer un vrai clic d'un
      // glisser pour orbiter (voir handlePointerUp).
      let pressAt = null;
      const handlePointerUp = (event) => {
        const press = pressAt;
        pressAt = null;
        if (!press || placementModeRef.current || !communeMesh || !communeMesh.visible) return;
        if (Math.hypot(event.clientX - press.x, event.clientY - press.y) > 5) return;
        // Les marqueurs (villes, prelevements, zones d'interet) ont deja
        // ouvert leur propre fiche au pointerdown : ne pas la remplacer.
        if (pick(event)) return;
        const index = communeAt();
        if (index < 0) return;
        setSelectedCommune(communeRecords[index]);
        setSelectedCity(null);
        setSelectedWithdrawal(null);
        setSelectedPoi(null);
        setSelectedStation(null);
        setSelectedDischarge(null);
      };
      const handlePointerDown = (event) => {
        markActive();
        pressAt = { x: event.clientX, y: event.clientY };
        // Reprendre la main sur la carte interrompt le vol en cours : rien
        // n'est plus desagreable qu'une camera qui continue son mouvement
        // alors qu'on essaie de la diriger.
        stateRef.current.poseTween = null;
        // Mode placement (edition d'une etape "points_of_interest") : le clic
        // ne selectionne rien, il pose un nouveau point via un raycast direct
        // contre le maillage terrain (jamais raycaste ailleurs dans ce fichier).
        if (placementModeRef.current) {
          const rect = renderer.domElement.getBoundingClientRect();
          pointerNdc.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
          pointerNdc.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
          raycaster.setFromCamera(pointerNdc, camera);
          const terrainHits = raycaster.intersectObject(terrain, false);
          if (terrainHits.length && onTerrainClickRef.current) {
            const p = terrainHits[0].point;
            onTerrainClickRef.current({ x: p.x, y: p.y, z: p.z });
          }
          return;
        }

        const hit = pick(event);
        // Une seule fiche ouverte a la fois : ouvrir celle du marqueur clique
        // ferme les autres, sinon deux panneaux se disputeraient l'ecran.
        const openOnly = (setter, value) => {
          [setSelectedCity, setSelectedWithdrawal, setSelectedPoi,
           setSelectedCommune, setSelectedStation, setSelectedDischarge].forEach((fn) => {
            if (fn !== setter) fn(null);
          });
          setter(value);
        };
        if (hit?.type === "city") openOnly(setSelectedCity, cityState.markers[hit.index]?.data || null);
        else if (hit?.type === "withdrawal") openOnly(setSelectedWithdrawal, withdrawalMeta[hit.index].data);
        else if (hit?.type === "poi") {
          const poiMarkers = stateRef.current.poiMarkers || [];
          openOnly(setSelectedPoi, poiMarkers[hit.index]?.data || null);
        } else if (hit?.type === "climate") openOnly(setSelectedStation, climateStations.meta[hit.index].data);
        else if (hit?.type === "discharge") openOnly(setSelectedDischarge, dischargeMarkers.meta[hit.index].data);
        // Un clic dans le vide ne ferme rien : c'est aussi le premier evenement
        // d'un glisser pour orbiter, et la fiche ouverte disparaissait des
        // qu'on bougeait la carte. On la ferme par sa croix, ou en ouvrant
        // une autre fiche.
      };
      renderer.domElement.addEventListener("pointermove", handlePointerMove);
      renderer.domElement.addEventListener("pointerdown", handlePointerDown);
      renderer.domElement.addEventListener("pointerup", handlePointerUp);
      renderer.domElement.addEventListener("pointerleave", handlePointerLeave);
      // Le zoom molette n'émet aucun événement de pointeur : sans cette
      // ligne, un zoom sans déplacer la souris se ferait à cadence bridée.
      const handleWheel = () => markActive();
      renderer.domElement.addEventListener("wheel", handleWheel, { passive: true });
      // Mise en veille du portable, changement de carte graphique sur un Mac :
      // le contexte WebGL est perdu puis rendu, et toutes les textures avec.
      // La carte d'ombre n'étant plus recalculée d'elle-même, il faut la
      // redemander explicitement — sinon la scène revient sans ses ombres,
      // définitivement.
      const handleContextRestored = () => invalidateShadows();
      renderer.domElement.addEventListener("webglcontextrestored", handleContextRestored);

      let frameId = 0;
      let lastRenderAt = 0;
      const clock = new THREE.Clock();
      const animate = () => {
        frameId = requestAnimationFrame(animate);

        // ECRAN RELACHE : la scene reste montee mais plus personne ne la
        // regarde (cf. TerritoryStage.js). La boucle continue de tourner, elle,
        // pour que la reprise soit immediate — c'est le rendu qu'on coupe, pas
        // le fil.
        if (pausedRef.current) return;

        const nowMs = performance.now();

        // BRIDAGE, À DEUX NIVEAUX.
        //
        // `maxFps` vient de la VUE : en arrière-plan d'une page de jeu, la
        // carte se dispute le fil principal avec React alors que personne ne
        // la manipule. C'est un plafond imposé, il gagne sur tout le reste.
        //
        // À défaut, le plafond dépend de ce qui se passe RÉELLEMENT à l'écran.
        // Une carte posée pendant qu'un animateur commente n'a aucune raison
        // d'être redessinée soixante fois par seconde : ce qui bouge alors
        // (l'eau qui file dans les rivières, la respiration des pastilles)
        // bouge lentement, et se lit aussi bien à 30. Dès qu'on saisit la
        // carte — orbite, zoom, survol — ou qu'un vol de caméra est en cours,
        // le plafond saute : c'est en mouvement rapide, et seulement là, que
        // la différence se voit.
        //
        // La rotation automatique ne compte PAS comme une manipulation. À 0,35
        // de vitesse le décor tourne d'un centième de degré par image ; la
        // brider est précisément le cas où l'on gagne le plus pour rien.
        const active = nowMs < activeUntil || Boolean(stateRef.current.poseTween);
        const fpsCap = maxFpsRef.current || (active ? 0 : IDLE_FPS);
        // La marge d'une milliseconde évite qu'un plafond à 30 sur un écran à
        // 60 Hz ne tombe à 20 : sans elle, une image arrivant à 33,2 ms est
        // rejetée et la suivante n'arrive qu'à 49,9 ms.
        if (fpsCap && nowMs - lastRenderAt < 1000 / fpsCap - 1) return;
        lastRenderAt = nowMs;

        // UN SEUL RELEVÉ D'HORLOGE PAR IMAGE, ET DANS CET ORDRE.
        // `Clock.getElapsedTime()` appelle `getDelta()` en interne : le lire en
        // premier consommait l'intervalle, et le `getDelta()` suivant ne
        // mesurait plus que les quelques microsecondes séparant les deux
        // lignes. `dt` valait donc ~0 et l'eau ne descendait pas les rivières.
        // On prend le delta, puis on lit le temps cumulé que ce même appel
        // vient de mettre à jour.
        const dt = clock.getDelta();
        const t = clock.elapsedTime;

        // Vitesse exprimée par seconde, pas par image : le défilement garde la
        // même allure à 30 comme à 60 (cf. IDLE_FPS).
        riverTexture.offset.x = (riverTexture.offset.x - dt * 0.12) % 1;

        // Transition de vue en cours : la camera est pilotee image par image,
        // la rotation automatique est suspendue le temps du vol (sinon les
        // deux se disputent l'azimut et l'arrivee derape).
        const tweening = stepPoseTween(stateRef.current, camera, controls, performance.now());
        // Rotation : imposee par la vue (fond de carte qui tourne pendant une
        // mosaique) ou, a defaut, laissee a l'interrupteur de l'animateur.
        // Toujours suspendue pendant un vol de camera.
        const forcedRotate = forcedRotateRef.current;
        if (forcedRotate != null) {
          controls.autoRotate = forcedRotate > 0 && !tweening;
          controls.autoRotateSpeed = forcedRotate;
        } else {
          controls.autoRotate = autoRotateOnRef.current && !tweening;
          controls.autoRotateSpeed = 0.35;
        }

        // Recentrage optique : rattrape la cible en douceur (le panneau
        // d'etape glisse en 0,28 s, la camera suit au meme rythme) puis se
        // cale exactement dessus pour ne plus recalculer la projection.
        const shiftTarget = insetsToViewShift(contentInsetsRef.current, mount.clientWidth, mount.clientHeight);
        const dx = shiftTarget.dx - viewShift.dx;
        const dy = shiftTarget.dy - viewShift.dy;
        if (Math.abs(dx) > 0.25 || Math.abs(dy) > 0.25) {
          viewShift.dx += dx * 0.14;
          viewShift.dy += dy * 0.14;
          applyCameraViewShift(camera, mount.clientWidth, mount.clientHeight, viewShift.dx, viewShift.dy);
        } else if (dx !== 0 || dy !== 0) {
          viewShift.dx = shiftTarget.dx;
          viewShift.dy = shiftTarget.dy;
          applyCameraViewShift(camera, mount.clientWidth, mount.clientHeight, viewShift.dx, viewShift.dy);
        }

        cityState.markers.forEach((m, i) => {
          const target = i === hoveredMarkerIndex ? 1.3 : 1;
          m.hoverScale = THREE.MathUtils.lerp(m.hoverScale ?? 1, target, 0.15);
          const pulse = 1 + Math.sin(t * 1.6 + i * 1.7) * 0.06;
          m.ring.scale.setScalar(m.hoverScale * pulse);
          m.ring.material.opacity = (i === hoveredMarkerIndex ? 1 : 0.75) * (0.85 + Math.sin(t * 1.6 + i * 1.7) * 0.1);
        });
        occludeCityLabels(cityState.markers, camera, mount.clientWidth, mount.clientHeight);

        // Lu depuis stateRef (pas une closure figee) : les marqueurs de
        // zones d'interet sont reconstruits apres la construction initiale
        // (effet pointsKey, cf. plus bas) quand on change d'etape.
        // Respiration lente de l'anneau, comme les reperes de ville. (Il a
        // d'abord tourne sur lui-meme facon reticule : trop appuye.)
        (stateRef.current.poiMarkers || []).forEach((m, i) => {
          const pulse = 1 + Math.sin(t * 1.4 + i * 1.3) * 0.07;
          m.lockRing.scale.setScalar(pulse);
          m.lockRing.material.opacity = 0.5 + Math.sin(t * 1.4 + i * 1.3) * 0.08;
        });

        // Panneau du point d'interet selectionne : suit la projection ecran
        // du marqueur (etiquette) pendant l'orbite/zoom, coude + panneau
        // repositionnes en manipulation DOM directe (pas de re-render React).
        const activePoi = selectedPoiRef.current;
        const panelEl = poiPanelElRef.current;
        if (activePoi && panelEl) {
          const marker = (stateRef.current.poiMarkers || []).find((m) => m.data.point_id === activePoi.point_id);
          if (marker) {
            marker.anchor.getWorldPosition(poiProjectVector);
            poiEdgeVector.copy(poiProjectVector);
            poiEdgeVector.y += marker.anchorRadius * 0.5;
            poiProjectVector.project(camera);
            poiEdgeVector.project(camera);
            if (poiProjectVector.z < 1) {
              const sx = (poiProjectVector.x * 0.5 + 0.5) * mount.clientWidth;
              const sy = (-poiProjectVector.y * 0.5 + 0.5) * mount.clientHeight;
              const syTop = (-poiEdgeVector.y * 0.5 + 0.5) * mount.clientHeight;
              const badgeRadiusPx = Math.max(8, Math.abs(sy - syTop));
              updatePoiPanelPosition(
                panelEl, poiLineElRef.current,
                sx, sy, badgeRadiusPx, mount.clientWidth, mount.clientHeight
              );
            } else {
              hidePoiPanel(panelEl, poiLineElRef.current);
            }
          } else {
            hidePoiPanel(panelEl, poiLineElRef.current);
          }
        } else if (panelEl) {
          hidePoiPanel(panelEl, poiLineElRef.current);
        }

        controls.update();
        renderer.render(scene, camera);
      };
      animate();

      // ARRÊT DE LA BOUCLE. `frameId` change à chaque image ; le nettoyage doit
      // donc annuler la valeur COURANTE, pas celle qu'on aurait recopiée dans
      // `stateRef` au montage. C'était le cas jusqu'ici, et l'identifiant
      // recopié était déjà consommé quand on l'annulait : la boucle survivait
      // au démontage et continuait de dessiner une scène détruite, une de plus
      // à chaque changement de territoire ou de jeu de couches.
      const stopLoop = () => {
        if (frameId) cancelAnimationFrame(frameId);
        frameId = 0;
      };

      const handleResize = () => {
        if (!mount) return;
        camera.aspect = mount.clientWidth / mount.clientHeight;
        camera.updateProjectionMatrix();
        // Le decalage optique est exprime en pixels : a re-appliquer avec les
        // nouvelles dimensions, sinon updateProjectionMatrix ci-dessus le perd.
        applyCameraViewShift(camera, mount.clientWidth, mount.clientHeight, viewShift.dx, viewShift.dy);
        renderer.setSize(mount.clientWidth, mount.clientHeight);
        // Les etiquettes de villes ont ete tracees pour une hauteur de vue
        // donnee ; a taille d'ecran constante, elles suivent le redimension-
        // nement sans rien faire, mais leur texture se retrouve etiree ou
        // reduite. On ne les redessine que si l'ecart devient visible (passage
        // en plein ecran, bascule du rail au grand panneau), pas a chaque
        // pixel d'un redimensionnement a la souris : chaque etiquette est un
        // canvas de texte.
        const builtH = cityState.builtViewportHeight || 0;
        if (builtH && Math.abs(mount.clientHeight - builtH) / builtH > 0.2) {
          rebuildCities(settingsRef.current.cities);
        }
      };
      window.addEventListener("resize", handleResize);
      const resizeObserver = new ResizeObserver(handleResize);
      resizeObserver.observe(mount);

      stateRef.current = {
        renderer, scene, camera, controls, terrain, geometry, material,
        geologyMesh, geologyGeometry,
        geologyCodes, geologyRecords, geologyBaseColors,
        heights, zmin, zRange, worldDiag, cols, rows, stopLoop, invalidateShadows, markActive,
        handleResize, resizeObserver,
        wallMesh, wallEdges, socleDepthRaw, fogObj,
        riversGroup, riverRibbons, riverMaterial, riverTexture, riverPickPlane,
        riverMaxHalfWidth, riverMinHalfWidth, riverDrape, rebuildRivers,
        citiesGroup, cityState, rebuildCities, builtCityKey: initialCityKey,
        withdrawalMesh, withdrawalHitMesh, withdrawalMeta, withdrawalDummy,
        poiGroup, poiMarkers: initialPoiMarkers, poiHitMeshes: initialPoiMarkers.map((m) => m.hitMesh),
        communeMesh, communeGeometry, communeBorderMesh, communeHighlightMesh,
        communeCodes, communesData, communeRecords, communeCells, communeBorders,
        communeBaseColors, hoveredCommuneIndex: -1, applyCommuneHover,
        climateMesh, climateGeometry, climateInterpolator, climateStations, climatePointList,
        dischargeMarkers, dischargeList,
        handlePointerMove, handlePointerDown, handlePointerUp, handlePointerLeave, handleWheel,
        handleContextRestored,
      };
    }

    load();

    return () => {
      cancelled = true;
      const s = stateRef.current;
      if (s.stopLoop) s.stopLoop();
      if (s.handleResize) window.removeEventListener("resize", s.handleResize);
      if (s.resizeObserver) s.resizeObserver.disconnect();
      if (s.renderer && s.handlePointerMove) s.renderer.domElement.removeEventListener("pointermove", s.handlePointerMove);
      if (s.renderer && s.handlePointerDown) s.renderer.domElement.removeEventListener("pointerdown", s.handlePointerDown);
      if (s.renderer && s.handlePointerUp) s.renderer.domElement.removeEventListener("pointerup", s.handlePointerUp);
      if (s.renderer && s.handlePointerLeave) s.renderer.domElement.removeEventListener("pointerleave", s.handlePointerLeave);
      if (s.renderer && s.handleWheel) s.renderer.domElement.removeEventListener("wheel", s.handleWheel);
      if (s.renderer && s.handleContextRestored) s.renderer.domElement.removeEventListener("webglcontextrestored", s.handleContextRestored);
      if (s.controls) s.controls.dispose();
      if (s.renderer) {
        s.renderer.dispose();
        if (mountRef.current && s.renderer.domElement.parentNode === mountRef.current) {
          mountRef.current.removeChild(s.renderer.domElement);
        }
      }
      [
        s.geometry, s.geologyGeometry, s.wallMesh?.geometry,
        s.communeGeometry, s.communeBorderMesh?.geometry, s.communeHighlightMesh?.geometry,
        s.climateGeometry, s.climateStations?.mesh.geometry,
      ].forEach((g) => g && g.dispose());
      [
        s.material, s.geologyMesh?.material, s.wallMesh?.material,
        s.communeMesh?.material, s.communeBorderMesh?.material, s.communeHighlightMesh?.material,
        s.climateMesh?.material, s.climateStations?.mesh.material, s.climateStations?.hitMesh.material,
      ].forEach((m) => m && m.dispose());
      (s.riverRibbons || []).forEach(({ geometry }) => geometry.dispose());
      if (s.riverMaterial) s.riverMaterial.dispose();
      if (s.riverTexture) s.riverTexture.dispose();
      (s.cityState?.markers || []).forEach(disposeCityMarker);
      if (s.withdrawalMesh) {
        s.withdrawalMesh.geometry.dispose();
        s.withdrawalMesh.material.dispose();
      }
      if (s.withdrawalHitMesh) s.withdrawalHitMesh.material.dispose();
      (s.poiMarkers || []).forEach(disposePoiMarker);
      stateRef.current = {};
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [territoryId, availableLayersKey]);

  // --- calques reellement visibles = ceux de l'etape, corriges par ce que
  // l'animateur a allume en seance. ---
  const visibleLayers = LAYER_ORDER.filter((id) =>
    id in layerOverrides ? layerOverrides[id] : (activeLayers || []).includes(id)
  );
  const visibleLayersKey = visibleLayers.join(",");
  const isLayerVisible = (id) => visibleLayers.includes(id);
  // Seules les couches dont les donnees ont ete chargees peuvent etre
  // allumees : le reste n'existe pas dans la scene.
  const togglableLayers = LAYER_ORDER.filter((id) => (availableLayers || []).includes(id));

  const toggleLayer = (id) => {
    setLayerOverrides((prev) => ({ ...prev, [id]: !isLayerVisible(id) }));
  };

  // Le recentrage optique est une animation de caméra comme une autre : quand
  // le panneau d'étape change de mode, la carte glisse pour se dégager en
  // 0,28 s. Sans cette ligne elle le ferait à la cadence de repos, et ce
  // glissement-là, la salle le voit à chaque étape.
  const contentInsetsKey = `${contentInsets?.left || 0}|${contentInsets?.right || 0}|${contentInsets?.top || 0}|${contentInsets?.bottom || 0}`;
  useEffect(() => {
    const s = stateRef.current;
    if (s.markActive) s.markActive();
  }, [contentInsetsKey]);

  // Changement d'etape : on repart de la vue voulue par l'auteur, calques ET
  // reglages. C'est ce qui garantit qu'une improvisation en seance ne fuit
  // jamais sur l'etape suivante.
  useEffect(() => {
    setLayerOverrides({});
    setAnimatorSettings({});
  }, [activeLayersKey, authoredSettingsKey]);

  useEffect(() => {
    const s = stateRef.current;
    if (!s.riversGroup) return;
    const active = new Set(visibleLayers);
    s.riversGroup.visible = active.has("rivers");
    if (s.citiesGroup) s.citiesGroup.visible = active.has("cities");
    if (s.geologyMesh) {
      s.geologyMesh.visible = active.has("geology");
      if (!active.has("geology")) setHoveredGeology(null);
    }
    if (s.dischargeMarkers) {
      const dischargeOn = active.has("discharge");
      s.dischargeMarkers.mesh.visible = dischargeOn;
      s.dischargeMarkers.hitMesh.visible = dischargeOn;
      if (!dischargeOn) setSelectedDischarge(null);
    }
    if (s.withdrawalMesh) s.withdrawalMesh.visible = active.has("withdrawals");
    if (s.withdrawalHitMesh) s.withdrawalHitMesh.visible = active.has("withdrawals");
    if (s.communeMesh) {
      const communesOn = active.has("communes");
      s.communeMesh.visible = communesOn;
      s.communeBorderMesh.visible = communesOn && communeBordersOn;
      s.communeHighlightMesh.visible = communesOn;
      // Eteindre la couche doit aussi effacer ce qu'elle avait surligne/ouvert.
      if (!communesOn) {
        s.applyCommuneHover(-1);
        setHoveredCommune(null);
        setSelectedCommune(null);
      }
    }
    if (s.climateMesh) {
      const climateOn = active.has("climate");
      s.climateMesh.visible = climateOn;
      if (s.climateStations) {
        s.climateStations.mesh.visible = climateOn && climateStationsOn;
        s.climateStations.hitMesh.visible = climateOn && climateStationsOn;
      }
      if (!climateOn) setSelectedStation(null);
    }
    // Prélèvements, stations climat et débit projettent une ombre : allumer ou
    // éteindre leur couche change ce que la carte d'ombre doit contenir.
    if (s.invalidateShadows) s.invalidateShadows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleLayersKey, communeBordersOn, climateStationsOn, status]);

  useEffect(() => {
    const s = stateRef.current;
    if (s.communeMesh) s.communeMesh.material.opacity = communeOpacity;
  }, [communeOpacity, status]);

  useEffect(() => {
    const s = stateRef.current;
    if (s.climateMesh) s.climateMesh.material.opacity = climateOpacity;
  }, [climateOpacity, status]);

  useEffect(() => {
    const s = stateRef.current;
    if (s.geologyMesh) s.geologyMesh.material.opacity = geologyOpacity;
  }, [geologyOpacity, status]);

  // --- variable / horizon / periode / LISSAGE climatique : reinterpole la
  // nappe et recolore les stations.
  //
  // Changer de variable ou de mois ne coute qu'une somme ponderee sur la
  // grille intermediaire (les poids sont deja calcules). Changer le LISSAGE,
  // lui, oblige a recalculer ces poids — d'ou l'appel a `setSmoothing`, qui
  // ne fait rien si la valeur n'a pas bouge. Sur cette grille (~3 900 noeuds
  // x 88 points), le recalcul reste assez leger pour suivre un curseur. ---
  useEffect(() => {
    const s = stateRef.current;
    if (!s.climateMesh || !s.climateInterpolator || !climateVariableId || !climateHorizon) return;
    const variable = climateAxes.variables.find((v) => v.id === climateVariableId);
    if (!variable) return;
    s.climateInterpolator.setSmoothing(climateSmoothingKm * 1000);
    const domain = climateDomain(s.climatePointList, variable, climatePeriod, climateAxes.horizons);
    const values = climateValues(s.climatePointList, variable, climateHorizon, climatePeriod);
    const colorAttribute = s.climateGeometry.attributes.color;
    s.climateInterpolator.fillColors(colorAttribute.array, values, domain, variable.ramp);
    colorAttribute.needsUpdate = true;
    if (s.climateStations) paintClimateStations(s.climateStations, variable, climateHorizon, climatePeriod, domain);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [climateVariableId, climateHorizon, climatePeriod, climateSmoothingKm, status]);

  // --- indicateur communal : recolore la nappe a partir du meme raster
  // (aucune geometrie reconstruite, aucun fichier retelecharge) ---
  useEffect(() => {
    const s = stateRef.current;
    if (!s.communeMesh || !s.communeRecords?.length || !communeIndicatorId) return;
    const { colors } = resolveIndicator(communeIndicators, s.communeRecords, communeIndicatorId);
    s.applyCommuneHover(-1); // sinon la teinte de survol serait figee dans les nouvelles couleurs
    s.communeBaseColors = colors;
    const colorAttribute = s.communeGeometry.attributes.color;
    paintRasterColors(colorAttribute.array, s.communeCodes, colors);
    colorAttribute.needsUpdate = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [communeIndicatorId, status]);

  // --- classement geologique : recolore la nappe depuis le meme raster
  // d'entites. Quatre lectures du sous-sol, un seul fichier. ---
  useEffect(() => {
    const s = stateRef.current;
    if (!s.geologyMesh || !s.geologyRecords?.length || !geologyIndicatorId) return;
    const { colors } = resolveIndicator(geologyIndicators, s.geologyRecords, geologyIndicatorId);
    s.geologyBaseColors = colors;
    const colorAttribute = s.geologyGeometry.attributes.color;
    paintRasterColors(colorAttribute.array, s.geologyCodes, colors);
    colorAttribute.needsUpdate = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geologyIndicatorId, status]);

  // --- grandeur / horizon de debit : recolore les marqueurs. L'echelle est
  // commune aux quatre horizons (bornes calculees par le pipeline), sinon
  // l'evolution jusqu'a 2100 serait invisible. ---
  useEffect(() => {
    const s = stateRef.current;
    if (!s.dischargeMarkers || !dischargeMetricId || !dischargeHorizon) return;
    const metric = dischargeAxes.metrics.find((m) => m.id === dischargeMetricId);
    if (metric) paintDischargeStations(s.dischargeMarkers, metric, dischargeHorizon);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dischargeMetricId, dischargeHorizon, status]);

  // --- epaisseur des cours d'eau / longueur de reference ---
  //
  // DEUX TEMPS, ET C'EST VOULU. Le curseur doit repondre sous le doigt :
  // `applyRiverWidth` se contente de deplacer des sommets deja la, c'est
  // instantane. Mais la finesse de la geometrie est taillee pour une largeur
  // donnee (cf. RIVER_RESAMPLE_RATIO) ; l'elargir sans la refaire ramenerait
  // le froissement dans les meandres. On rebatit donc une fois le curseur
  // repose — reconstruire a chaque pixel parcouru serait injouable.
  useEffect(() => {
    const s = stateRef.current;
    if (!s.riverRibbons?.length || !riverLengthCapKm) return undefined;
    const widthPerKm = s.riverMaxHalfWidth / riverLengthCapKm;
    s.riverRibbons.forEach((ribbon) => {
      const lengthKm = ribbon.river?.length_km || 0;
      const raw = Math.max(s.riverMinHalfWidth, widthPerKm * Math.min(lengthKm, riverLengthCapKm));
      ribbon.currentHalfWidth = raw * riverWidthScale;
      applyRiverWidth(ribbon, ribbon.currentHalfWidth, s.riverDrape);
    });
    const timer = setTimeout(() => {
      const cur = stateRef.current;
      if (cur.rebuildRivers) cur.rebuildRivers(riverLengthCapKm, riverWidthScale);
    }, RIVER_REBUILD_DELAY_MS);
    return () => clearTimeout(timer);
  }, [riverWidthScale, riverLengthCapKm, status]);

  // --- selection des villes : reconstruit les marqueurs quand elle change.
  //
  // La cle est la SELECTION RESULTANTE, pas les reglages : glisser le curseur
  // d'espacement de 0 a 10 km emet des dizaines d'evenements mais ne produit
  // qu'une poignee de selections distinctes. Comparer le resultat plutot que
  // l'entree evite donc de reconstruire 40 etiquettes a chaque pixel. ---
  const citySelectionKey = useMemo(
    () => selectCities(cityList, settings.cities).map((c) => `${c.code_insee}:${c.tier}`).join(","),
    [cityList, settings.cities]
  );
  useEffect(() => {
    const s = stateRef.current;
    if (!s.rebuildCities || s.builtCityKey === citySelectionKey) return;
    s.rebuildCities(settingsRef.current.cities);
    s.builtCityKey = citySelectionKey;
    setSelectedCity(null); // sa fiche pourrait rester ouverte sur une ville retiree
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [citySelectionKey, status]);

  // --- usages de prelevement masques + dimensions des cylindres. La hauteur
  // est reechelonnee sur les seules categories visibles, pour que masquer un
  // usage redonne de l'amplitude aux autres. ---
  const hiddenUsagesKey = hiddenUsages.join(",");
  useEffect(() => {
    const s = stateRef.current;
    if (!s.withdrawalMesh || !s.withdrawalMeta?.length) return;
    const hidden = new Set(hiddenUsages);
    recomputeWithdrawalScales(s.withdrawalMeta, hidden, s.worldDiag, s.zRange, barWidthScale, barHeightScale);
    s.withdrawalMeta.forEach((wmeta) => {
      wmeta.hiddenByFilter = hidden.has(wmeta.data.usage);
      applyWithdrawalTransform(s.withdrawalMesh, s.withdrawalDummy, wmeta, FIXED_EXAGGERATION, wmeta.baseRadius);
      applyWithdrawalTransform(s.withdrawalHitMesh, s.withdrawalDummy, wmeta, FIXED_EXAGGERATION, wmeta.hitRadius);
    });
    s.withdrawalMesh.instanceMatrix.needsUpdate = true;
    s.withdrawalHitMesh.instanceMatrix.needsUpdate = true;
    // Les cylindres viennent de changer de hauteur et de rayon.
    if (s.invalidateShadows) s.invalidateShadows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hiddenUsagesKey, barWidthScale, barHeightScale, status]);

  // --- changement de pose de camera APRES construction (etape suivante).
  //
  // C'est ici que se joue la difference entre un diaporama et un survol : au
  // lieu de teleporter la camera, on lance une transition que `animate` fait
  // avancer image par image. Le premier positionnement, lui, reste instantane
  // (`animateCamera` a false, ou aucune pose de depart) : voler depuis une
  // vue par defaut que personne n'a vue n'aurait aucun sens. ---
  useEffect(() => {
    const s = stateRef.current;
    if (!s.camera || !s.controls || !cameraPose) return;
    const from = readPoseFromScene(s.camera, s.controls);
    if (!animateCamera || !from) {
      applyPoseToScene(s.camera, s.controls, cameraPose);
      return;
    }
    s.poseTween = {
      from,
      to: { ...cameraPose, target: cameraPose.target || { x: 0, y: 0, z: 0 } },
      startedAt: performance.now(),
      duration: POSE_TRANSITION_MS,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraPoseKey]);

  // --- brouillard : valeur de depart posee par l'auteur (content.fog_enabled),
  // que le joueur peut ensuite basculer depuis les reglages. ---
  useEffect(() => {
    setFogOn(fogEnabled);
  }, [fogEnabled]);

  useEffect(() => {
    const s = stateRef.current;
    if (!s.scene) return;
    s.scene.fog = fogOn ? s.fogObj : null;
  }, [fogOn, status]);

  // La rotation automatique n'a plus d'effet dedie : `animate` la (re)pose a
  // chaque image depuis `autoRotateOnRef`, parce qu'elle doit aussi etre
  // suspendue pendant une transition de vue.

  // --- points d'interet APRES construction (ex: etape suivante avec des
  // points differents) : reconstruit uniquement le groupe de marqueurs, pas
  // toute la scene. Sans effet au tout premier montage (s.poiGroup pas
  // encore cree) puisque la construction initiale s'en charge deja (voir
  // initScene, valeur lue via pointsRef). ---
  useEffect(() => {
    const s = stateRef.current;
    if (!s.poiGroup) return;
    (s.poiMarkers || []).forEach((m) => {
      s.poiGroup.remove(m.group);
      disposePoiMarker(m);
    });
    const newMarkers = (points || []).map((point, index, all) => createPoiMarker(point, all.length > 1 ? index : null, s.worldDiag));
    newMarkers.forEach((m, i) => {
      m.hitMesh.userData.markerIndex = i;
      s.poiGroup.add(m.group);
    });
    s.poiMarkers = newMarkers;
    s.poiHitMeshes = newMarkers.map((m) => m.hitMesh);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pointsKey]);

  useEffect(() => {
    setFocusedOuvrageIndex(0);
  }, [selectedWithdrawal]);

  // --- effacement du rappel des commandes ---
  useEffect(() => {
    if (status !== "ready" || hintDismissed) return undefined;
    const mount = mountRef.current;
    const dismiss = () => setHintDismissed(true);
    const timer = setTimeout(dismiss, 9000);
    mount?.addEventListener("pointerdown", dismiss);
    mount?.addEventListener("wheel", dismiss, { passive: true });
    return () => {
      clearTimeout(timer);
      mount?.removeEventListener("pointerdown", dismiss);
      mount?.removeEventListener("wheel", dismiss);
    };
  }, [status, hintDismissed]);

  const toggleUsage = (usage) => {
    const next = hiddenUsages.includes(usage)
      ? hiddenUsages.filter((u) => u !== usage)
      : [...hiddenUsages, usage];
    setLayerSetting("withdrawals", "usages", next);
  };

  const focusedOuvrage = selectedWithdrawal?.ouvrages?.[focusedOuvrageIndex];
  const showGeology = isLayerVisible("geology");
  const showWithdrawals = isLayerVisible("withdrawals");
  const showCommunes = isLayerVisible("communes") && communes.length > 0;
  const showClimate = isLayerVisible("climate") && climatePoints.length > 0;
  const showDischarge = isLayerVisible("discharge") && dischargeStations.length > 0;
  const climateVariable = climateAxes.variables.find((v) => v.id === climateVariableId) || null;
  const climateScale = climateVariable
    ? climateDomain(climatePoints, climateVariable, climatePeriod, climateAxes.horizons)
    : null;
  const climatePeriodIndex = Math.max(0, climateAxes.periods.indexOf(climatePeriod));
  // Indicateurs, legendes et groupes de selection : tous derives des
  // SPECIFICATIONS embarquees dans le datapack (bornes observees, modalites
  // presentes, couleurs de source), jamais d'un catalogue ecrit en dur ici.
  // Un indicateur ajoute cote pipeline apparait sans toucher a ce fichier.
  const communeIndicator = findIndicator(communeIndicators, communeIndicatorId);
  const communeLegend = useMemo(() => buildLegend(communeIndicator), [communeIndicator]);
  const communeIndicatorGroups = useMemo(
    () => groupedIndicators(communeIndicators), [communeIndicators]
  );
  const geologyIndicator = findIndicator(geologyIndicators, geologyIndicatorId);
  const geologyLegend = useMemo(() => buildLegend(geologyIndicator), [geologyIndicator]);
  const geologyIndicatorGroups = useMemo(
    () => groupedIndicators(geologyIndicators), [geologyIndicators]
  );
  const dischargeMetric = dischargeAxes.metrics.find((m) => m.id === dischargeMetricId) || null;
  const dischargeLegend = useMemo(() => buildLegend(dischargeMetric), [dischargeMetric]);
  const selectedCultures = selectedCommune?.["agriculture.cultures.postes"] || [];
  const hasAnyLegend =
    !!meta || showCommunes || showClimate || showDischarge ||
    (!!geologyIndicator && showGeology) || (usageList.length > 0 && showWithdrawals);
  /**
   * FICHE OUVERTE D'ELLE-MEME. Une page localisee n'a qu'un lieu et n'a rien a
   * faire attendre un clic : elle arrive fiche ouverte, ce qui est aussi ce
   * qu'un animateur veut en projetant. Le clic reste possible pour la rouvrir
   * apres l'avoir fermee.
   *
   * Suspendu tant que la scene n'est pas prete : les marqueurs n'existent pas
   * encore, et le panneau se positionne sur eux a chaque image.
   */
  useEffect(() => {
    if (status !== "ready" || !openPointId) return;
    const point = (points || []).find((p) => p.point_id === openPointId);
    if (point) setSelectedPoi(point);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, openPointId, pointsKey]);

  // Le numero ne dit quelque chose que s'il y en a plusieurs a distinguer : sur
  // un lieu unique, une pastille « 1 » est du bruit.
  const numberedPoints = (points || []).length > 1;
  const selectedPoiNumber = selectedPoi && numberedPoints
    ? (points || []).findIndex((p) => p.point_id === selectedPoi.point_id) + 1
    : 0;
  const zoomedPoiNumber = zoomedPoi && numberedPoints
    ? (points || []).findIndex((p) => p.point_id === zoomedPoi.point_id) + 1
    : 0;
  // Aucun usage masque = tous visibles, qui est aussi l'etat par defaut.
  const allUsagesActive = hiddenUsages.length === 0;

  // En-tete de section de legende : le libelle + le bouton ⓘ, qui ouvre la
  // fiche de la couche (source, methode, limites). Un seul helper pour les
  // six sections, sinon le bouton finirait par manquer sur l'une d'elles.
  const legendHead = (layerId, label) => (
    <>
      <span className="terrain3d-eyebrow">{label ?? layerLabel(layerId)}</span>
      {layerId && layerInfo(layerId) && (
        <button
          type="button"
          className={`terrain3d-infobtn${openInfoLayer === layerId ? " active" : ""}`}
          onClick={() => setOpenInfoLayer(openInfoLayer === layerId ? null : layerId)}
          title="D'où viennent ces données ?"
          aria-label={`Informations sur la couche ${layerLabel(layerId)}`}
          aria-expanded={openInfoLayer === layerId}
        >
          i
        </button>
      )}
    </>
  );

  return (
    <div
      className="terrain-viewer"
      // Les memes inserts que ceux appliques a la camera, exposes au CSS : les
      // panneaux centres en bas (ville, prelevement, rappel des commandes) se
      // recentrent eux aussi sur la zone visible, sinon ils passent sous le
      // panneau d'etape sur un ecran etroit.
      style={{
        "--t3d-inset-left": `${contentInsets?.left || 0}px`,
        "--t3d-inset-right": `${contentInsets?.right || 0}px`,
        // Facteur de lisibilite : la carte est PROJETEE devant une salle, et
        // une legende en 11 px y est illisible au-dela du deuxieme rang. Un
        // seul facteur pilote toute la typographie et la largeur du panneau,
        // plutot qu'une retouche taille par taille (cf. TerrainViewer.css).
        "--t3d-ui-scale": uiScale,
      }}
    >
      <div className="terrain-viewer-canvas" ref={mountRef} />
      {/* Assombrissement des bords : donne de la profondeur au relief et
          detache les panneaux du fond, qui sont sinon poses sur un aplat. */}
      <div className="terrain-viewer-vignette" aria-hidden="true" />

      {status === "loading" && (
        <div className="terrain3d-loading">
          <div className="terrain3d-loading-bar">
            <div className="terrain3d-loading-fill" />
          </div>
          <p>Chargement de la carte...</p>
        </div>
      )}

      {status === "error" && (
        <div className="terrain3d-loading">
          <p>Impossible de charger la carte de ce territoire.</p>
        </div>
      )}

      {/* PANNEAUX DU VIEWER. Ils n'ont de sens que si la carte est ce qu'on
          regarde. En fond d'une page de jeu (`chrome: false`), une colonne de
          legendes flottant par-dessus une mosaique de decisions serait un
          contresens : il ne reste alors que le relief. */}
      {status === "ready" && chrome === "full" && (
        <>
          {/* Colonne droite : un seul objet (reglages + legendes) plutot que
              trois boites flottantes de largeurs differentes. L'en-tete porte
              le repli, les legendes sont des sections separees par un filet. */}
          <div className="terrain3d-mappanel">
            <div className="terrain3d-mappanel-head">
              {hasAnyLegend ? (
                <button
                  type="button"
                  className="terrain3d-mappanel-toggle"
                  onClick={() => setLegendsVisible((v) => !v)}
                  aria-expanded={legendsVisible}
                >
                  <span className="terrain3d-eyebrow">Légendes</span>
                  <span className={`terrain3d-chevron${legendsVisible ? " open" : ""}`} aria-hidden="true" />
                </button>
              ) : (
                <span className="terrain3d-eyebrow">Carte</span>
              )}
              {togglableLayers.length > 0 && (
                <button
                  type="button"
                  className={`terrain3d-iconbtn${layerMenuOpen ? " active" : ""}`}
                  onClick={() => setLayerMenuOpen((v) => !v)}
                  title="Ajouter ou retirer une couche"
                  aria-label="Ajouter ou retirer une couche"
                  aria-expanded={layerMenuOpen}
                >
                  +
                </button>
              )}
              <button
                type="button"
                className={`terrain3d-iconbtn${showSettingsMenu ? " active" : ""}`}
                onClick={() => setShowSettingsMenu((v) => !v)}
                title="Réglages de la carte"
                aria-label="Réglages de la carte"
                aria-expanded={showSettingsMenu}
              >
                ⚙
              </button>
            </div>

            {/* MENU DE L'ANIMATEUR. Ce n'est pas un outil de composition —
                les vues se fabriquent dans le builder — mais une trappe
                d'improvisation : quelqu'un demande "et la géologie ?", il
                faut y arriver en deux clics sans quitter la carte projetée.
                Les couches sont groupées et libellées pour ça. */}
            {layerMenuOpen && togglableLayers.length > 0 && (
              <div className="terrain3d-mappanel-section">
                <span className="terrain3d-eyebrow">Couches</span>
                {togglableLayers.map((id) => (
                  <div key={id} className="terrain3d-layerrow">
                    <label className="terrain3d-switchrow">
                      <input type="checkbox" checked={isLayerVisible(id)} onChange={() => toggleLayer(id)} />
                      <span>{layerLabel(id)}</span>
                    </label>
                    {layerInfo(id) && (
                      <button
                        type="button"
                        className={`terrain3d-infobtn${openInfoLayer === id ? " active" : ""}`}
                        onClick={() => setOpenInfoLayer(openInfoLayer === id ? null : id)}
                        title={`D'où viennent les données « ${layerLabel(id)} » ?`}
                        aria-label={`Informations sur la couche ${layerLabel(id)}`}
                        aria-expanded={openInfoLayer === id}
                      >
                        i
                      </button>
                    )}
                  </div>
                ))}
                {openInfoLayer && <LayerInfo layerId={openInfoLayer} info={layerInfo(openInfoLayer)} />}
              </div>
            )}

            {/* Réglages : ceux de la SCÈNE (brume, rotation) puis, couche par
                couche, ceux du registre — rendus par le même composant que le
                composer du builder, donc jamais désynchronisés. Seules les
                couches réellement à l'écran apparaissent, sinon ce menu
                devient une liste de curseurs sans effet visible. */}
            {showSettingsMenu && (
              <div className="terrain3d-mappanel-section">
                <label className="terrain3d-switchrow">
                  <input
                    type="checkbox"
                    checked={autoRotateOn}
                    onChange={(e) => setAutoRotateOn(e.target.checked)}
                  />
                  <span>Rotation automatique</span>
                </label>
                <label className="terrain3d-switchrow">
                  <input type="checkbox" checked={fogOn} onChange={(e) => setFogOn(e.target.checked)} />
                  <span>Brume atmosphérique</span>
                </label>

                {visibleLayers.map((id) => {
                  if (!LAYER_SPECS[id]?.controls.length) return null;
                  return (
                    <div key={id} className="terrain3d-settinggroup">
                      <span className="terrain3d-settinggroup-title">{layerLabel(id)}</span>
                      <LayerControlGroup
                        layerId={id}
                        settings={settings[id]}
                        catalog={catalog}
                        onChange={(controlId, value) => setLayerSetting(id, controlId, value)}
                        // L'indicateur, la variable et l'horizon se choisissent
                        // dans la LÉGENDE : c'est elle qui décrit ce qu'ils
                        // peignent, les séparer obligerait à faire l'aller-retour.
                        exclude={["indicator", "variable", "horizon", "period", "metric"]}
                      />
                    </div>
                  );
                })}
              </div>
            )}

            {hasAnyLegend && legendsVisible && (
              <div className="terrain3d-mappanel-body">
                {meta && (
                  <section className="terrain3d-mappanel-section">
                    <span className="terrain3d-eyebrow">Altitude</span>
                    <div className="terrain3d-legend-gradient" />
                    <div className="terrain3d-legend-labels">
                      <span>{meta.zmin.toFixed(0)} m</span>
                      <span>{meta.zmax.toFixed(0)} m</span>
                    </div>
                  </section>
                )}

                {showCommunes && (
                  <section className="terrain3d-mappanel-section">
                    <div className="terrain3d-sectionhead">{legendHead("communes")}</div>
                    {openInfoLayer === "communes" && <LayerInfo layerId="communes" info={layerInfo("communes")} />}
                    {/* Le selecteur est ici, et pas dans les reglages : c'est
                        lui qui dit ce que la legende juste en dessous decrit. */}
                    <select
                      className="terrain3d-select"
                      value={communeIndicatorId}
                      onChange={(e) => setLayerSetting("communes", "indicator", e.target.value)}
                      aria-label="Indicateur communal affiché"
                    >
                      {communeIndicatorGroups.map(({ name, indicators }) => (
                        <optgroup key={name} label={name}>
                          {indicators.map((indicator) => (
                            <option key={indicator.id} value={indicator.id}>
                              {indicator.label}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>

                    <IndicatorLegend indicator={communeIndicator} legend={communeLegend} />
                  </section>
                )}

                {showClimate && climateVariable && (
                  <section className="terrain3d-mappanel-section">
                    <div className="terrain3d-sectionhead">{legendHead("climate")}</div>
                    {openInfoLayer === "climate" && <LayerInfo layerId="climate" info={layerInfo("climate")} />}
                    <select
                      className="terrain3d-select"
                      value={climateVariableId}
                      onChange={(e) => setLayerSetting("climate", "variable", e.target.value)}
                      aria-label="Variable climatique affichée"
                    >
                      {climateAxes.variables.map((variable) => (
                        <option key={variable.id} value={variable.id}>{variable.label}</option>
                      ))}
                    </select>

                    {/* Horizons en boutons plutôt qu'en liste déroulante :
                        comparer actuel / 2030 / 2050 est le geste principal
                        de cette couche, il doit tenir en un clic. */}
                    <div className="terrain3d-segmented" role="group" aria-label="Horizon">
                      {climateAxes.horizons.map((horizon) => (
                        <button
                          key={horizon}
                          type="button"
                          className={`terrain3d-segment${horizon === climateHorizon ? " is-active" : ""}`}
                          onClick={() => setLayerSetting("climate", "horizon", horizon)}
                          aria-pressed={horizon === climateHorizon}
                        >
                          {horizonLabel(horizon)}
                        </button>
                      ))}
                    </div>

                    <label className="terrain3d-slider">
                      <span>{periodLabel(climatePeriod)}</span>
                      <input
                        type="range"
                        min={0}
                        max={climateAxes.periods.length - 1}
                        step={1}
                        value={climatePeriodIndex}
                        onChange={(e) => setLayerSetting("climate", "period", climateAxes.periods[parseInt(e.target.value, 10)])}
                        aria-label="Période affichée"
                      />
                    </label>

                    <div
                      className="terrain3d-legend-gradient"
                      style={{ background: rampCss(climateVariable.ramp) }}
                    />
                    <div className="terrain3d-legend-labels">
                      <span>{formatClimateValue(climateVariable, climateScale.min)}</span>
                      <span>{formatClimateValue(climateVariable, climateScale.max)}</span>
                    </div>
                    <p className="terrain3d-sectionnote">
                      {climateVariable.source}. Échelle commune aux {climateAxes.horizons.length}{" "}
                      horizons pour cette période, afin que le changement se voie.
                    </p>
                  </section>
                )}

                {geologyIndicator && showGeology && (
                  <section className="terrain3d-mappanel-section">
                    <div className="terrain3d-sectionhead">{legendHead("geology")}</div>
                    {openInfoLayer === "geology" && <LayerInfo layerId="geology" info={layerInfo("geology")} />}
                    {/* Quatre classements du meme sous-sol : de quelle
                        formation s'agit-il, y a-t-il de l'eau, circule-t-elle
                        vite, est-elle protegee. */}
                    <select
                      className="terrain3d-select"
                      value={geologyIndicatorId || ""}
                      onChange={(e) => setLayerSetting("geology", "indicator", e.target.value)}
                      aria-label="Classement géologique affiché"
                    >
                      {geologyIndicatorGroups.map(({ name, indicators }) => (
                        <optgroup key={name} label={name}>
                          {indicators.map((indicator) => (
                            <option key={indicator.id} value={indicator.id}>{indicator.label}</option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                    <IndicatorLegend indicator={geologyIndicator} legend={geologyLegend} />
                  </section>
                )}

                {showDischarge && dischargeMetric && (
                  <section className="terrain3d-mappanel-section">
                    <div className="terrain3d-sectionhead">{legendHead("discharge")}</div>
                    {openInfoLayer === "discharge" && <LayerInfo layerId="discharge" info={layerInfo("discharge")} />}
                    <select
                      className="terrain3d-select"
                      value={dischargeMetricId || ""}
                      onChange={(e) => setLayerSetting("discharge", "metric", e.target.value)}
                      aria-label="Grandeur de débit affichée"
                    >
                      {dischargeAxes.metrics.map((metric) => (
                        <option key={metric.id} value={metric.id}>{metric.label}</option>
                      ))}
                    </select>

                    {/* Quatre horizons ici, contre trois pour le climat : la
                        chronique de débit va jusqu'en 2100. */}
                    <div className="terrain3d-segmented" role="group" aria-label="Horizon de débit">
                      {dischargeAxes.horizons.map((horizon) => (
                        <button
                          key={horizon}
                          type="button"
                          className={`terrain3d-segment${horizon === dischargeHorizon ? " is-active" : ""}`}
                          onClick={() => setLayerSetting("discharge", "horizon", horizon)}
                          aria-pressed={horizon === dischargeHorizon}
                        >
                          {horizonLabel(horizon)}
                        </button>
                      ))}
                    </div>

                    <IndicatorLegend
                      indicator={dischargeMetric}
                      legend={dischargeLegend}
                      note={`${dischargeStations.length} stations · échelle commune aux ${dischargeAxes.horizons.length} horizons. La hauteur des marqueurs suit la même valeur que leur couleur.`}
                    />
                  </section>
                )}

                {usageList.length > 0 && showWithdrawals && (
                  <section className="terrain3d-mappanel-section">
                    <div className="terrain3d-sectionhead">
                      {legendHead("withdrawals", "Prélèvements par usage")}
                      <button
                        type="button"
                        className="terrain3d-textbtn"
                        onClick={() => setLayerSetting("withdrawals", "usages", allUsagesActive ? usageList.map((u) => u.usage) : [])}
                      >
                        {allUsagesActive ? "Aucun" : "Tout"}
                      </button>
                    </div>
                    <div className="terrain3d-chiplist">
                      {usageList.map(({ usage, color, count }) => {
                        const isActive = !hiddenUsages.includes(usage);
                        return (
                          <button
                            key={usage}
                            type="button"
                            className={`terrain3d-chip terrain3d-chip--filter${isActive ? "" : " is-off"}`}
                            style={{ "--chip-color": color }}
                            onClick={() => toggleUsage(usage)}
                            aria-pressed={isActive}
                            title={usage}
                          >
                            <span className="terrain3d-usage-dot" />
                            <span className="terrain3d-chip-label">{usageShortLabel(usage)}</span>
                            <span className="terrain3d-chip-count">{count}</span>
                          </button>
                        );
                      })}
                    </div>
                    {openInfoLayer === "withdrawals" && (
                      <LayerInfo layerId="withdrawals" info={layerInfo("withdrawals")} />
                    )}
                  </section>
                )}
              </div>
            )}
          </div>

          <div className={`terrain3d-hud terrain3d-hud-hint${hintDismissed ? " is-hidden" : ""}`}>
            Glissez pour tourner autour, molette pour zoomer, cliquez un marqueur pour le détail
          </div>

          {hoveredRiver && (
            <div
              className="terrain3d-river-tooltip"
              style={{ left: hoveredRiver.clientX, top: hoveredRiver.clientY }}
            >
              {hoveredRiver.name}
              {hoveredRiver.length_km != null && (
                <span className="terrain3d-river-tooltip-length"> · {hoveredRiver.length_km.toFixed(1)} km</span>
              )}
            </div>
          )}

          {showCommunes && hoveredCommune && (
            <div
              className="terrain3d-river-tooltip"
              style={{ left: hoveredCommune.clientX, top: hoveredCommune.clientY }}
            >
              {hoveredCommune.commune.nom_commune}
              <span className="terrain3d-river-tooltip-length">
                {" "}· {formatIndicatorValue(communeIndicator, hoveredCommune.commune)}
              </span>
            </div>
          )}

          {selectedCity && (
            <div className="terrain3d-hud terrain3d-hud-cityinfo">
              <button className="terrain3d-cityinfo-close" onClick={() => setSelectedCity(null)} aria-label="Fermer">
                ×
              </button>
              <span className="terrain3d-badge">
                {selectedCity.code_insee || selectedCity.code_postal}
              </span>
              <h2>{selectedCity.name}</h2>
              <p>
                {selectedCity.population != null
                  ? `${formatNumber(selectedCity.population, "count")} habitants · ${selectedCity.rank}ᵉ commune du département`
                  : selectedCity.statut}
              </p>
            </div>
          )}

          {showGeology && hoveredGeology && (
            <div
              className="terrain3d-river-tooltip"
              style={{ left: hoveredGeology.clientX, top: hoveredGeology.clientY }}
            >
              {hoveredGeology.entity["theme.libelle"] || "Entité"}
              <span className="terrain3d-river-tooltip-length">
                {" "}· {formatIndicatorValue(geologyIndicator, hoveredGeology.entity)}
              </span>
              <div className="terrain3d-tooltip-detail">{hoveredGeology.entity.libelle}</div>
            </div>
          )}

          {showCommunes && selectedCommune && (
            <div className="terrain3d-hud terrain3d-hud-communeinfo">
              <button
                className="terrain3d-cityinfo-close"
                onClick={() => setSelectedCommune(null)}
                aria-label="Fermer"
              >
                ×
              </button>
              <span className="terrain3d-badge">{selectedCommune.code_insee}</span>
              <h2>{selectedCommune.nom_commune}</h2>
              <p className="terrain3d-subtitle">
                {formatIndicatorValue(findIndicator(communeIndicators, "population.habitants"), selectedCommune)}
                {selectedCommune["population.rang_departement"] != null &&
                  ` · ${selectedCommune["population.rang_departement"]}ᵉ du département`}
                {selectedCommune.surface_ha != null && ` · ${Math.round(selectedCommune.surface_ha)} ha`}
              </p>

              {selectedCommune["agriculture.specialisation.libelle"] && (
                <p className="terrain3d-commune-spec">
                  <span
                    className="terrain3d-usage-dot"
                    style={{ "--chip-color": selectedCommune["agriculture.specialisation.couleur"] || "#8d9aa0" }}
                  />
                  {selectedCommune["agriculture.specialisation.libelle"]}
                </p>
              )}

              {/* Le clic sert a comparer une commune sur TOUT ce que le
                  territoire renseigne, pas seulement sur l'indicateur qui
                  colore la carte. Les groupes viennent du datapack : ajouter
                  une thematique cote pipeline la fait apparaitre ici. */}
              {communeIndicatorGroups.map(({ name, indicators }) => {
                const rows = indicators.filter((indicator) => selectedCommune[indicator.id] != null);
                if (!rows.length) return null;
                return (
                  <div key={name} className="terrain3d-commune-group">
                    <h3 className="terrain3d-panel-subhead">{name}</h3>
                    <dl className="terrain3d-commune-stats">
                      {rows.map((indicator) => (
                        <div
                          key={indicator.id}
                          className={indicator.id === communeIndicatorId ? "is-current" : ""}
                        >
                          <dt>{indicator.label}</dt>
                          <dd>{formatIndicatorValue(indicator, selectedCommune)}</dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                );
              })}

              {/* Les postes de culture ne se cartographient pas (un tableau
                  n'a pas de couleur) mais ils expliquent la spécialisation
                  affichée : c'est leur place, dans la fiche. */}
              {selectedCultures.length > 0 && (
                <>
                  <h3 className="terrain3d-panel-subhead">
                    Cultures {selectedCommune["agriculture.cultures.millesime"] &&
                      `(RA ${selectedCommune["agriculture.cultures.millesime"]})`}
                  </h3>
                  <div className="terrain3d-culture-list">
                    {selectedCultures.map((poste) => (
                      <div key={poste.libelle} className="terrain3d-culture-row">
                        <span className="terrain3d-culture-name">{poste.libelle}</span>
                        <span className="terrain3d-culture-bar">
                          <span style={{ width: `${Math.min(100, poste.part_sau_pct || 0)}%` }} />
                        </span>
                        <span className="terrain3d-culture-value">
                          {poste.surface_ha == null
                            ? "secret stat."
                            : `${formatNumber(poste.part_sau_pct, "decimal")} %`}
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              )}

            </div>
          )}


          {showClimate && selectedStation && climateVariable && (
            <div className="terrain3d-hud terrain3d-hud-stationinfo">
              <button
                className="terrain3d-cityinfo-close"
                onClick={() => setSelectedStation(null)}
                aria-label="Fermer"
              >
                ×
              </button>
              <span className="terrain3d-badge">Point de mesure</span>
              <h2>{climateVariable.label}</h2>
              <p className="terrain3d-subtitle">
                {selectedStation.latitude?.toFixed(3)}° N · {selectedStation.longitude?.toFixed(3)}° E
              </p>

              {/* C'est ici que la donnée mensuelle prend son sens : sur la
                  carte on ne voit qu'un mois à la fois, la courbe montre
                  l'année entière et les trois horizons d'un coup. */}
              <div className="terrain3d-station-chart">
                <Line
                  data={{
                    labels: CLIMATE_MONTH_LABELS,
                    datasets: stationMonthlySeries(selectedStation, climateVariable, climateAxes.horizons).map(
                      (serie, i) => ({
                        label: horizonLabel(serie.horizon),
                        data: serie.values,
                        borderColor: HORIZON_COLORS[i % HORIZON_COLORS.length],
                        backgroundColor: HORIZON_COLORS[i % HORIZON_COLORS.length],
                        borderWidth: 2,
                        pointRadius: 0,
                        pointHoverRadius: 4,
                        tension: 0.35,
                      })
                    ),
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    interaction: { mode: "index", intersect: false },
                    plugins: {
                      legend: { display: false },
                      // Le plugin d'étiquettes est enregistré globalement :
                      // sans ça, 36 valeurs se superposeraient sur la courbe.
                      datalabels: { display: false },
                      tooltip: {
                        callbacks: {
                          label: (ctx) => `${ctx.dataset.label} : ${formatClimateValue(climateVariable, ctx.parsed.y)}`,
                        },
                      },
                    },
                    scales: {
                      x: { ticks: { color: "#c8dbe0", font: { size: 10 } }, grid: { display: false } },
                      y: {
                        ticks: { color: "#c8dbe0", font: { size: 10 } },
                        grid: { color: "rgba(255,255,255,0.08)" },
                      },
                    },
                  }}
                />
              </div>

              <dl className="terrain3d-commune-stats">
                {climateAxes.horizons.map((horizon, i) => (
                  <div key={horizon} className={horizon === climateHorizon ? "is-current" : ""}>
                    <dt>
                      <span
                        className="terrain3d-usage-dot"
                        style={{ "--chip-color": HORIZON_COLORS[i % HORIZON_COLORS.length] }}
                      />
                      {horizonLabel(horizon)} · cumul annuel
                    </dt>
                    <dd>
                      {formatClimateValue(
                        climateVariable,
                        climateValueAt(selectedStation, climateVariable, horizon, "annuel")
                      )}
                    </dd>
                  </div>
                ))}
              </dl>

              {/* Le bilan de l'horizon affiché : la carte montre un flux à la
                  fois, ces quelques chiffres disent où l'eau part réellement. */}
              {stationScalars(selectedStation, climateHorizon).length > 0 && (
                <>
                  <h3 className="terrain3d-panel-subhead">
                    Bilan hydrique · {horizonLabel(climateHorizon)}
                  </h3>
                  <dl className="terrain3d-commune-stats">
                    {stationScalars(selectedStation, climateHorizon).map((scalar) => (
                      <div key={scalar.id} title={scalar.help || undefined}>
                        <dt>{scalar.label}</dt>
                        <dd>{scalar.text}</dd>
                      </div>
                    ))}
                  </dl>
                </>
              )}
            </div>
          )}

          {showDischarge && selectedDischarge && (
            <div className="terrain3d-hud terrain3d-hud-stationinfo">
              <button
                className="terrain3d-cityinfo-close"
                onClick={() => setSelectedDischarge(null)}
                aria-label="Fermer"
              >
                ×
              </button>
              <span className="terrain3d-badge">Station hydrométrique</span>
              <h2>{selectedDischarge.cours_eau}</h2>
              <p className="terrain3d-subtitle">
                {selectedDischarge.lieu} · {selectedDischarge.code_station}
              </p>

              {/* Le régime mensuel, tous horizons superposés. En coefficients
                  de Pardé (débit du mois ÷ module), deux rivières de tailles
                  très différentes deviennent comparables : au-dessus de 1
                  c'est les hautes eaux, en dessous l'étiage. */}
              <label className="terrain3d-switchrow">
                <input
                  type="checkbox"
                  checked={dischargeNormalized}
                  onChange={(e) => setDischargeNormalized(e.target.checked)}
                />
                <span>Coefficients de Pardé (débit ÷ module)</span>
              </label>

              <div className="terrain3d-station-chart">
                <Line
                  data={{
                    labels: DISCHARGE_MONTH_LABELS,
                    datasets: stationMonthlyDischarge(
                      selectedDischarge, dischargeAxes.horizons, { normalized: dischargeNormalized }
                    ).map((serie, i) => ({
                      label: horizonLabel(serie.horizon),
                      data: serie.values,
                      borderColor: HORIZON_COLORS[i % HORIZON_COLORS.length],
                      backgroundColor: HORIZON_COLORS[i % HORIZON_COLORS.length],
                      borderWidth: 2,
                      pointRadius: 0,
                      pointHoverRadius: 4,
                      tension: 0.35,
                    })),
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    interaction: { mode: "index", intersect: false },
                    plugins: {
                      legend: { display: false },
                      datalabels: { display: false },
                      tooltip: {
                        callbacks: {
                          label: (ctx) =>
                            `${ctx.dataset.label} : ${formatNumber(ctx.parsed.y, "precise")}${
                              dischargeNormalized ? "" : " m³/s"
                            }`,
                        },
                      },
                    },
                    scales: {
                      x: { ticks: { color: "#c8dbe0", font: { size: 10 } }, grid: { display: false } },
                      y: {
                        ticks: { color: "#c8dbe0", font: { size: 10 } },
                        grid: { color: "rgba(255,255,255,0.08)" },
                      },
                    },
                  }}
                />
              </div>

              {/* Toutes les grandeurs déclarées, à tous les horizons : c'est
                  ici que se lit l'aggravation de l'étiage d'ici 2100. */}
              {dischargeAxes.metrics.map((metric) => (
                <div key={metric.id} className="terrain3d-commune-group">
                  <h3 className="terrain3d-panel-subhead">{metric.label}</h3>
                  <dl className="terrain3d-commune-stats">
                    {dischargeAxes.horizons.map((horizon, i) => (
                      <div key={horizon} className={horizon === dischargeHorizon ? "is-current" : ""}>
                        <dt>
                          <span
                            className="terrain3d-usage-dot"
                            style={{ "--chip-color": HORIZON_COLORS[i % HORIZON_COLORS.length] }}
                          />
                          {horizonLabel(horizon)}
                        </dt>
                        <dd>
                          {formatDischargeValue(metric, dischargeValueAt(selectedDischarge, metric, horizon))}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </div>
              ))}

              <p className="terrain3d-sectionnote">
                DRIAS-EAU / Explore2, chaîne SIM2, scénario RCP 8.5, débits simulés,
                pas observés. Les indicateurs annuels portent sur l'année hydrologique
                (1ᵉʳ août → 31 juillet).
              </p>
            </div>
          )}

          {selectedWithdrawal && focusedOuvrage && (
            <div className="terrain3d-hud terrain3d-hud-withdrawalinfo">
              <button
                className="terrain3d-cityinfo-close"
                onClick={() => setSelectedWithdrawal(null)}
                aria-label="Fermer"
              >
                ×
              </button>
              <span className="terrain3d-badge">{selectedWithdrawal.usage}</span>
              <h2>{selectedWithdrawal.commune}</h2>
              {selectedWithdrawal.ouvrages.length > 1 && (
                <p className="terrain3d-subtitle">
                  {selectedWithdrawal.ouvrages.length} ouvrages à cet endroit (mêmes coordonnées
                  communales), {formatVolume(selectedWithdrawal.total_volume)} au total
                </p>
              )}

              {selectedWithdrawal.ouvrages.length > 1 && (
                <div className="terrain3d-ouvrage-list">
                  {selectedWithdrawal.ouvrages.map((ouv, i) => (
                    <button
                      key={ouv.code_ouvrage}
                      className={`terrain3d-ouvrage-item${i === focusedOuvrageIndex ? " active" : ""}`}
                      onClick={() => setFocusedOuvrageIndex(i)}
                    >
                      <span className="terrain3d-ouvrage-name">{ouv.nom || ouv.code_ouvrage}</span>
                      <span className="terrain3d-ouvrage-vol">{formatVolume(ouv.last_volume)}</span>
                    </button>
                  ))}
                </div>
              )}

              <h2 className="terrain3d-ouvrage-focus-name">{focusedOuvrage.nom || "Ouvrage"}</h2>

              <div className="terrain3d-withdrawal-stats">
                <div>
                  <span className="terrain3d-stat-label">Dernier volume ({focusedOuvrage.last_annee})</span>
                  <span className="terrain3d-stat-value">{formatVolume(focusedOuvrage.last_volume)}</span>
                </div>
                {focusedOuvrage.trend_pct != null && (
                  <div>
                    <span className="terrain3d-stat-label">Évolution</span>
                    <span className="terrain3d-stat-value">
                      {focusedOuvrage.trend_pct > 0 ? "+" : ""}
                      {focusedOuvrage.trend_pct}%
                    </span>
                  </div>
                )}
              </div>

              {focusedOuvrage.series?.length > 1 && (
                <div className="terrain3d-withdrawal-chart">
                  <Bar
                    data={{
                      labels: focusedOuvrage.series.map((s) => s.annee),
                      datasets: [
                        {
                          data: focusedOuvrage.series.map((s) => s.volume),
                          backgroundColor:
                            usageList.find((u) => u.usage === selectedWithdrawal.usage)?.color || "#54a8ba",
                          borderRadius: 3,
                        },
                      ],
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: { display: false },
                        tooltip: { callbacks: { label: (ctx) => formatVolume(ctx.parsed.y) } },
                        datalabels: {
                          color: "#0a1a24",
                          font: { size: 10, weight: "700" },
                          anchor: "end",
                          align: "start",
                          formatter: (v) => formatVolume(v),
                        },
                      },
                      scales: {
                        x: { ticks: { color: "#c8dbe0", font: { size: 10 } }, grid: { display: false } },
                        y: {
                          ticks: { color: "#c8dbe0", font: { size: 10 }, callback: (v) => formatVolume(v) },
                          grid: { color: "rgba(255,255,255,0.08)" },
                        },
                      },
                    }}
                  />
                </div>
              )}

              {focusedOuvrage.uri && (
                <a href={focusedOuvrage.uri} target="_blank" rel="noreferrer" className="terrain3d-withdrawal-link">
                  Fiche officielle Sandre ↗
                </a>
              )}
            </div>
          )}

        </>
      )}

      {/* MARQUEURS ET LEUR FICHE — le seul habillage qui survit hors du mode
          plein cadre. Une page localisee (cf. TerritoryStage.js, mode
          `located`) montre un lieu et ce qui s'y passe : le marqueur et la
          fiche qui lui est reliee SONT le contenu, alors que les legendes et
          les reglages n'auraient rien a y faire. */}
      {status === "ready" && chrome !== "none" && (
        <>
            {selectedPoi && (
              <>
                {/* Connecteur (coude orthogonal) + point d'ancrage — suivent le
                    marqueur a l'ecran, repositionnes a chaque frame (animate()),
                    pas de React state pour ca (voir updatePoiPanelPosition). */}
                <svg className="terrain3d-poi-connector-svg" aria-hidden="true">
                  <polyline ref={poiLineElRef} className="terrain3d-poi-connector-line" points="" />
                </svg>

                <div ref={poiPanelElRef} className="terrain3d-poi-panel">
                  {/* Deux commandes, dans l'ordre du geste : agrandir (pour
                      montrer), fermer (pour rendre la carte). La fiche est
                      volontairement petite — elle commente un point du relief et
                      doit laisser voir ce qui l'entoure — donc l'image y arrive à
                      260 px : de quoi la reconnaître, pas de quoi la montrer à une
                      salle. L'agrandissement est la réponse (cf. ZoomOverlay). */}
                  <div className="terrain3d-poi-tools">
                    {(selectedPoi.image_url || selectedPoi.text) && (
                      <button
                        className="terrain3d-poi-tool"
                        onClick={() => setZoomedPoi(selectedPoi)}
                        title="Agrandir cette fiche"
                        aria-label="Agrandir cette fiche"
                      >
                        ⤢
                      </button>
                    )}
                    <button
                      className="terrain3d-poi-tool"
                      onClick={() => setSelectedPoi(null)}
                      title="Fermer"
                      aria-label="Fermer"
                    >
                      ×
                    </button>
                  </div>
                  {/* Le numéro repris du marqueur : c'est lui qui dit quelle
                      pastille de la carte cette fiche décrit, le trait ne fait
                      que confirmer. */}
                  <div className="terrain3d-poi-head">
                    {selectedPoiNumber > 0 && (
                      <span className="terrain3d-poi-number">{selectedPoiNumber}</span>
                    )}
                    <div className="terrain3d-poi-headtext">
                      <h2 className="terrain3d-poi-title">{selectedPoi.title || "Sans titre"}</h2>
                    </div>
                  </div>
                  {selectedPoi.image_url && (
                    <button
                      className="terrain3d-poi-imagebtn"
                      onClick={() => setZoomedPoi(selectedPoi)}
                      title="Agrandir l'image"
                    >
                      <img className="terrain3d-poi-image" src={selectedPoi.image_url} alt="" />
                    </button>
                  )}
                  {selectedPoi.text && <p className="terrain3d-poi-text">{selectedPoi.text}</p>}
                </div>
              </>
            )}

            {/* Hors du bloc `selectedPoi` : l'agrandissement est un plein écran,
                il n'a rien à faire dans une fiche ancrée au marqueur, dont la
                position est recalculée à chaque frame. */}
            {zoomedPoi && (
              <ZoomOverlay
                title={zoomedPoi.title || "Sans titre"}
                imageUrl={zoomedPoi.image_url}
                text={zoomedPoi.text}
                badge={
                  zoomedPoiNumber > 0 ? (
                    <span className="terrain3d-poi-number t3d-zoom-badge">{zoomedPoiNumber}</span>
                  ) : null
                }
                onClose={() => setZoomedPoi(null)}
              />
            )}
        </>
      )}
    </div>
  );
});

export default TerrainViewer;
